import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  RootCauseCategory,
  Section,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import {
  parseSupplementItem,
  systemicFormulasProductUrl,
} from '../../utils/supplementItem';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import { isSameBloodwork } from '../../utils/sameBloodwork';
import {
  SupplementsBlock,
  packSupplementBlocks,
} from '../../utils/pdfSupplementsPagination';
import {
  HOLOBIOME_ANCHOR,
  HOLOBIOME_KITS,
  SYSTEMIC_FORMULAS_BRAND,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import { prioritizeRootCauseCategories } from './further-considerations-section';
import { nutrientCategoryStatuses } from './nutrient-category-order';
import {
  Status,
  rankSectionsBySeverity,
  rankSectionsWithStatus,
  sectionShortLabel,
} from './results-overview-panel';

// Per-system PDF labels in canonical report order. Drives both the order of
// section blocks within each brand and the labels we show. Exported so the
// consolidated Section Notes panel gathers each section's note under the same
// friendly labels, in the same order, as this Supplements panel.
export const SECTION_HEADINGS: { section: Section; label: string }[] = [
  { section: Section.RBC,                           label: 'Blood Oxygen & Red Blood Cell Function' },
  { section: Section.IRON,                          label: 'Iron Status & Utilization' },
  { section: Section.WBC,                           label: 'Immune Activity' },
  { section: Section.PLATELETS,                     label: 'Blood Clotting & Flow' },
  { section: Section.GLUCOSE,                       label: 'Glucose Regulation & Metabolic Function' },
  { section: Section.KIDNEY,                        label: 'Kidney Function' },
  { section: Section.PROTEINS,                      label: 'Protein Status' },
  { section: Section.NUTRIENTS,                     label: 'Vitamin & Mineral Status' },
  { section: Section.ELECTROLYTES,                  label: 'Electrolyte Balance' },
  { section: Section.DIGESTION,                     label: 'Digestive & Gut Health' },
  { section: Section.LIVER_BILIARY,                 label: 'Liver Function' },
  { section: Section.LIPIDS,                        label: 'Lipid Balance' },
  { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation & Oxidative Stress' },
  { section: Section.THYROID,                       label: 'Thyroid Function' },
  { section: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal Balance' },
  { section: Section.SEX_HORMONE,                   label: 'Hormone Balance' },
  { section: Section.PROSTATE,                      label: 'Prostate Function' },
];

const BRAND_LABELS: { [brand: string]: string } = {
  systemicFormulas: 'Systemic Formulas',
  bwhLabs: 'BWH Labs',
};
const brandLabel = (brand: string): string => BRAND_LABELS[brand] ?? brand;

// Fixed display order for brand blocks: Systemic Formulas first, then BWH Labs,
// then any other/unknown brand alphabetically. Brands not listed here sort
// after the known ones. Used instead of an alphabetical sort so BWH Labs sits
// below Systemic Formulas rather than above it.
const BRAND_ORDER: string[] = ['systemicFormulas', 'bwhLabs'];
function compareBrands(a: string, b: string): number {
  const ia = BRAND_ORDER.indexOf(a);
  const ib = BRAND_ORDER.indexOf(b);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return brandLabel(a).localeCompare(brandLabel(b));
}

// A Supplements row's title reads "<Brand> — Supporting Supplement(s) for
// <Pattern>" (plural for multi-product brands like Systemic Formulas, singular
// for single-product ones like BWH Labs). Returns null when a title doesn't
// follow the convention, in which case the product simply carries no pattern
// attribution rather than a mangled one.
const PATTERN_TITLE_RE = / Supporting Supplements? for (.+)$/;

// The marker parenthetical some sheets append to a pattern title — the adrenal
// tab spells out which markers fired ("Adrenal Recovery Support (▼ Pregnenolone
// and ▼ DHEA-S)"). The legend names the PATTERN; which markers put the client in
// it is the section's own analysis to state, and repeating it here made a
// two-word legend entry run half a table wide.
//
// Keyed on the ARROW, not on the parentheses. Plenty of pattern names carry a
// legitimate trailing bracket the reader needs — "Low Vitamin A (Retinol)",
// "Low Vitamin D (25-OH)" — and a blanket strip would take the half of the name
// that says which assay. An arrow inside the bracket is only ever the marker
// list.
const PATTERN_MARKERS_RE = /\s*\([^)]*[\u25b2\u25bc][^)]*\)\s*$/;
function patternNameFromTitle(title?: string): string | null {
  if (!title) return null;
  const match = PATTERN_TITLE_RE.exec(title);
  return match ? match[1].replace(PATTERN_MARKERS_RE, '').trim() : null;
}

// Which brands an instance of this table covers. The Supplements panel and the
// Holobiome Roadmap section render the SAME table over two disjoint brand sets:
// the roadmap owns Systemic Formulas — its kits, its products, its protocol —
// and Supplements holds every other brand, so a product appears in exactly one
// of them.
//
// The split happens AFTER collection, not by narrowing the brand list going in,
// so each brand's month sequencing comes out identical to what it was when the
// two shared one panel: collectByBrandAndSection groups by brand and
// assignAddOnMonths already runs per brand.
export type SupplementsBrandScope = 'systemicFormulas' | 'otherBrands';

const inScope = (brand: string, scope: SupplementsBrandScope): boolean =>
  (brand === SYSTEMIC_FORMULAS_BRAND) === (scope === 'systemicFormulas');

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Defaults to the Supplements panel's half — every brand EXCEPT Systemic
  // Formulas, which the Holobiome Roadmap section renders instead.
  brandScope?: SupplementsBrandScope;
  // Card heading. Defaults to 'Supplements'; the roadmap's PDF card passes its
  // own name.
  title?: string;
  forPdf?: boolean;
  includeInPdf?: boolean;
  onIncludeInPdfToggle?: () => void;
  // Required for Edit-as-override. When absent, the Edit button is hidden.
  bloodworkEntryStore?: BloodworkEntryStore;
};

type State = {
  editing: boolean;
  draftText: string;
};

type Supplement = { name: string; description: string; link?: string };

// Which Holobiome kit a product ships in, if any. A client is on every kit, so
// a recommended product that sits inside one isn't a separate purchase — the
// panel says so rather than presenting it identically to a true add-on.
type KitRef = { month: number; name: string };

// A product appears ONCE in the whole panel, under the most concerning system
// that recommended it.
//
// `patternRefs` are 1-based indices into its own section's `patterns` legend —
// the numbers the Pattern column shows. Numbering rather than repeating the
// name is what keeps this readable: four of Glucose's products come from the
// same 50-character pattern, and printing it four times is the redundancy the
// per-row version had.
export type ProductRow = Supplement & {
  patternRefs: number[];
  // Other systems that also recommended it, worst-first. Noted under the
  // pattern numbers so the cross-system fact survives listing the product once.
  alsoSections: Section[];
  kit: KitRef | null;
  // When to start this product, 1–5. A kit product takes its kit's month; every
  // other product is placed by assignAddOnMonths below.
  month: number;
};

// One block per system, in severity order — worst first.
type SectionGroup = {
  section: Section;
  // The legend, in the order this system's patterns were encountered. Index i
  // is cited as number i+1.
  patterns: string[];
  products: ProductRow[];
};

export type BrandGroup = {
  brand: string;
  sections: SectionGroup[];
};

// Working shape during collection, before ownership and numbering are decided.
type Collected = Supplement & {
  sections: Section[];
  patternsBySection: Map<Section, string[]>;
  orderBySection: Map<Section, number>;
  kit: KitRef | null;
};

// product name → the kit it ships in, earliest month first when it's in more
// than one (ImmuneGenic is in both Prepare and Promote; the client meets it in
// Prepare). Built once — HOLOBIOME_KITS is a module constant.
const KIT_BY_PRODUCT: Map<string, KitRef> = (() => {
  const map = new Map<string, KitRef>();
  HOLOBIOME_KITS.forEach(kit => {
    kit.productNames.forEach(name => {
      const key = normalizeSupplementName(name);
      const existing = map.get(key);
      if (!existing || kit.month < existing.month) {
        map.set(key, { month: kit.month, name: kit.name });
      }
    });
  });
  return map;
})();

const kitFor = (name: string): KitRef | null =>
  KIT_BY_PRODUCT.get(normalizeSupplementName(name)) ?? null;

// The full system name, for the heading that opens each group — there's a whole
// row for it, so it can say "Glucose Regulation & Metabolic Function" rather
// than the compact word the Results Overview boxes use. The short form is still
// what the cramped spots get (the "also Immune" note in the Pattern column).
const SECTION_LABEL = new Map(
  SECTION_HEADINGS.map(({ section, label }) => [section, label] as const),
);
const sectionFullLabel = (section: Section): string =>
  SECTION_LABEL.get(section) ?? sectionShortLabel(section);

// The Holobiome Roadmap runs five months.
const MONTHS = 5;

// How many products that AREN'T in a kit to start in any one month. The whole
// point of sequencing is that a client doesn't open a box of sixteen bottles,
// so the cap is what actually does that work.
//
// PROVISIONAL — 3 is a placeholder until Systemic Formulas tells us what's
// reasonable to add on top of a full kit. Changing it re-flows the months and
// nothing else.
const ADD_ONS_PER_MONTH = 3;

// Give every product a start month.
//
// Kit products keep their kit's month and cost nothing against the cap — the
// client receives them that month regardless of what we say here.
//
// Everything else is placed by severity: `sections` arrives worst-first, so the
// products answering the most concerning system start in Month 1, the next
// system's in Month 2, and so on. Within that, a system with more add-ons than
// the cap spills forward month by month rather than dumping them all at once.
//
// NOT YET MODELLED: per-product clinical constraints ("don't introduce this one
// before Month 3"). Those are Mike's call and have no home in the sheet yet —
// see the "Add-on Mo" column. Until they exist, severity alone decides, which
// can place something later than a practitioner would like.
function assignAddOnMonths(sections: SectionGroup[]): void {
  const used = new Map<number, number>();
  // Product → the month it was scheduled for, so a product listed under more
  // than one system (see LIST EVERYWHERE below) is SCHEDULED once. Without
  // this the same bottle would show a different month under each system, and
  // would eat a cap slot per listing — sixteen listings of eight products
  // would push half of them to Month 5. `sections` arrives worst-first and a
  // row's own sections are sorted the same way, so the first group to reach a
  // product is the most concerning one that asked for it: exactly the group
  // that would have owned it when each product appeared only once.
  const placed = new Map<string, number>();
  sections.forEach((group, sectionIdx) => {
    const start = Math.min(sectionIdx + 1, MONTHS);
    group.products.forEach(product => {
      if (product.kit) {
        product.month = product.kit.month;
        return;
      }
      const key = normalizeSupplementName(product.name);
      const already = placed.get(key);
      if (already != null) {
        product.month = already;
        return;
      }
      let month = start;
      while (month < MONTHS && (used.get(month) ?? 0) >= ADD_ONS_PER_MONTH) {
        month += 1;
      }
      used.set(month, (used.get(month) ?? 0) + 1);
      product.month = month;
      placed.set(key, month);
    });
  });
}

// Plain-text form for the override seed. Unlike the rendered cell, this spells
// out "no kit" — the seed is prose a practitioner reads, where a trailing empty
// bracket would just look broken.
const kitLabel = (kit: KitRef | null): string =>
  kit ? `Holobiome Month ${kit.month}` : 'no kit';

// Thin alias over the shared parser so the rest of this file keeps reading
// parseItem(...). The separator/URL logic lives in src/utils/supplementItem.ts
// so this panel and FurtherConsiderationsSection can never drift apart.
function parseItem(item: string): Supplement | null {
  return parseSupplementItem(item);
}

// Product names are camel-cased ("ImmuneGenic", "MethylGenic", "EnerGenic"),
// and a camel-cased word carries no break opportunity — so in the Product
// column a name half a letter too wide got broken wherever the box ended, which
// printed "ImmuneGeni / c". Split at each lowercase→uppercase seam so the name
// can break as "Immune / Genic" instead.
//
// The seam is ASCII-only on purpose: "µBiomic" stays whole, because µ is not
// [a-z] and "Biomic" alone reads as a different product.
function camelSegments(name: string): string[] {
  const segments: string[] = [];
  let start = 0;
  for (let i = 1; i < name.length; i += 1) {
    const prev = name[i - 1];
    const ch = name[i];
    if (ch >= 'A' && ch <= 'Z' && prev >= 'a' && prev <= 'z') {
      segments.push(name.slice(start, i));
      start = i;
    }
  }
  segments.push(name.slice(start));
  return segments;
}

// <wbr> rather than a soft hyphen or a zero-width space: it is a break
// OPPORTUNITY the layout only takes when the name doesn't fit, and it adds no
// character to the text — so a name that fits is untouched, and copy, search
// and textContent still see "ImmuneGenic".
function withCamelBreaks(name: string): React.ReactNode {
  const segments = camelSegments(name);
  if (segments.length === 1) return name;
  return segments.map((segment, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={i}>
      {i > 0 && <wbr />}
      {segment}
    </React.Fragment>
  ));
}

// A product name, everywhere one is shown: bold, and a link when we have a URL
// for it. No color of its own — it takes Bootstrap's link color, which is what
// keeps every product name in the report the same blue. Exported so the
// roadmap's Add-On Products table renders its names through this too; the two
// tables sit one above the other, and hard-coding a hex in either is how they'd
// drift apart.
export function renderSupplementName(s: {
  name: string;
  link?: string;
}): JSX.Element {
  if (!s.link) return <strong>{withCamelBreaks(s.name)}</strong>;
  return (
    <a
      href={s.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontWeight: 'bold' }}
    >
      {withCamelBreaks(s.name)}
    </a>
  );
}

// Collect every recommended product once per brand, ordered by how concerning
// the system that recommended it is.
//
// `severityOrder` is the section ranking from the Results Overview (worst
// first). Sections missing from it — untested, or with no valued markers — sort
// after the ranked ones in canonical report order, so nothing silently
// disappears if the ranking can't see a section.
// LIST EVERYWHERE (`listInEverySystem`, the default).
//
// A product recommended by three systems is listed under all three. The table
// used to file it under the most concerning one and leave the others to a small
// "also Glucose" note beside its pattern number — which meant a practitioner
// reading the Immune group could not see the immune recommendation at all if
// some other system happened to outrank Immune that day. What belongs in a
// system's list is what that system's patterns asked for, full stop.
//
// The "also …" note stays either way, now naming the OTHER systems a listing
// appears under. It is the thing that stops a repeat being read as two bottles.
//
// Scheduling is unaffected: assignAddOnMonths places each product once, so the
// month reads the same under every system it appears in, and the Targeted
// Additions boxes on the rail still count each product once. Pass false for a
// caller that wants one row per product (collectScheduledAddOns does).
function collectByBrandAndSection(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
  severityOrder: Section[],
  listInEverySystem = true,
): BrandGroup[] {
  const byBrand = new Map<string, Map<string, Collected>>();
  // "<brand>::<section>" → how many distinct products that system has
  // contributed so far, i.e. the next product's position in its list.
  const positionInSection = new Map<string, number>();

  const severityRank = (section: Section): number => {
    const idx = severityOrder.indexOf(section);
    // Unranked sections go after every ranked one, then by report order.
    if (idx !== -1) return idx;
    const fallback = SECTION_HEADINGS.findIndex(h => h.section === section);
    return severityOrder.length + (fallback === -1 ? SECTION_HEADINGS.length : fallback);
  };

  // Walk in canonical report order; the severity sort below reorders the result.
  // Within a section, the same category order the Supplements tab uses, so the
  // first product a pattern lists is the first one seen here — that order is
  // the clinical priority and survives into the table.
  SECTION_HEADINGS.forEach(({ section }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    const ordered = prioritizeRootCauseCategories(
      section,
      categories,
      bloodworkAnalysis,
    );
    ordered.forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        const brand = consideration.brand || 'unknown';
        const patternName = patternNameFromTitle(consideration.title);
        (consideration.items || []).forEach(item => {
          // resolveSupplement handles both a supplement-library tag (resolved
          // via findByTag) and a legacy inline "Name — Description — url"
          // string. Returns null for the "Use code …" promo, which must not
          // reach the panel as a product.
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed?.name) return;

          if (!byBrand.has(brand)) byBrand.set(brand, new Map());
          const products = byBrand.get(brand)!;
          const key = normalizeSupplementName(parsed.name);

          // Next slot in this system's list, claimed the first time the system
          // mentions the product.
          const posKey = `${brand}::${section}`;
          const claimPosition = (row: Collected) => {
            if (row.orderBySection.has(section)) return;
            const next = positionInSection.get(posKey) ?? 0;
            row.orderBySection.set(section, next);
            positionInSection.set(posKey, next + 1);
          };

          const notePattern = (row: Collected) => {
            if (!patternName) return;
            const list = row.patternsBySection.get(section) ?? [];
            if (!list.includes(patternName)) list.push(patternName);
            row.patternsBySection.set(section, list);
          };

          const existing = products.get(key);
          if (existing) {
            // Same product, another pattern or another system: record the extra
            // origin rather than repeating the row.
            if (!existing.sections.includes(section)) {
              existing.sections.push(section);
            }
            claimPosition(existing);
            notePattern(existing);
            return;
          }
          const row: Collected = {
            name: parsed.name,
            description: parsed.description || '',
            // Every product name is a link. The catalog URL when the sheet
            // carries one; otherwise, for Systemic Formulas only, the slug
            // guess — so a product that hasn't been wired up yet still reaches
            // its page rather than sitting there as dead bold text. Another
            // brand's product without a URL stays plain, since guessing an SF
            // slug for it would link somewhere that doesn't sell it.
            link:
              parsed.link ||
              (brand === 'systemicFormulas'
                ? systemicFormulasProductUrl(parsed.name)
                : undefined),
            sections: [section],
            patternsBySection: new Map(),
            kit: kitFor(parsed.name),
            orderBySection: new Map(),
          };
          claimPosition(row);
          notePattern(row);
          products.set(key, row);
        });
      });
    });
  });

  // Ownership, grouping, and numbering. A product belongs to the most
  // concerning system that recommended it; each system then numbers only the
  // patterns its OWN products cite, so the legend never lists a pattern with
  // nothing under it.
  return Array.from(byBrand.entries())
    .map(([brand, collected]) => {
      const rows = Array.from(collected.values());
      rows.forEach(row =>
        row.sections.sort((a, b) => severityRank(a) - severityRank(b)),
      );

      // Every system that asked for it, or only the most concerning one.
      // row.sections is already sorted worst-first, so [0] is the owner.
      const bySection = new Map<Section, Collected[]>();
      rows.forEach(row => {
        const homes = listInEverySystem ? row.sections : [row.sections[0]];
        homes.forEach(section => {
          const list = bySection.get(section) ?? [];
          list.push(row);
          bySection.set(section, list);
        });
      });

      const sections: SectionGroup[] = Array.from(bySection.entries())
        .sort(([a], [b]) => severityRank(a) - severityRank(b))
        .map(([section, owned]) => {
          // Products in the order this system's patterns listed them — the
          // clinical priority.
          owned.sort(
            (a, b) =>
              (a.orderBySection.get(section) ?? 0) -
              (b.orderBySection.get(section) ?? 0),
          );

          const patterns: string[] = [];
          const products = owned.map(row => {
            const refs = (row.patternsBySection.get(section) ?? []).map(name => {
              let idx = patterns.indexOf(name);
              if (idx === -1) idx = patterns.push(name) - 1;
              return idx + 1; // 1-based, as displayed
            });
            return {
              name: row.name,
              description: row.description,
              link: row.link,
              patternRefs: refs,
              // The OTHER systems this product appears under, not "everything
              // after the owner" — in the listing under Glucose for a product
              // owned by Immune, the note has to read "also Immune".
              alsoSections: row.sections.filter(s => s !== section),
              kit: row.kit,
              month: 0, // set by assignAddOnMonths once every group exists
            };
          });
          return { section, patterns, products };
        });

      assignAddOnMonths(sections);
      return { brand, sections };
    })
    .filter(g => g.sections.length > 0)
    .sort((a, b) => compareBrands(a.brand, b.brand));
}

// A non-kit recommendation, with the month assignAddOnMonths scheduled it for
// and the system(s) that asked for it — everything the roadmap's per-month
// "Targeted Additions" box needs to render one row.
export type ScheduledAddOn = {
  name: string;
  description: string;
  link?: string;
  month: number;
  // Worst-first, the recommending system before any others that also asked.
  sections: Section[];
};

// The Systemic Formulas recommendations that are in NO kit, keyed by the month
// they start.
//
// This reads the SAME collection the by-system table renders, so the month the
// roadmap prints under a kit and the month that table prints in its Month
// column come from one computation and cannot drift. That matters more than it
// looks: assignAddOnMonths places products by severity with a per-month cap and
// spill-forward, so the schedule is not derivable from a product's identity —
// it depends on the whole test.
//
// A product recommended by two systems is scheduled once, under the worse one,
// which is the order collectByBrandAndSection already produces.
export function collectScheduledAddOns(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<number, ScheduledAddOn[]> {
  const byMonth = new Map<number, ScheduledAddOn[]>();
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return byMonth;
  const seen = new Set<string>();
  scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
    'systemicFormulas',
    // One row per product: the rail parks each addition under a single month,
    // and its origins line already names every system that asked for it. The
    // `seen` guard below would collapse the repeats anyway; asking for them
    // and then throwing them away is just a slower way to the same answer.
    false,
  ).forEach(({ sections }) =>
    sections.forEach(group =>
      group.products.forEach(product => {
        if (product.kit) return;
        const key = normalizeSupplementName(product.name);
        if (seen.has(key)) return;
        seen.add(key);
        const list = byMonth.get(product.month) ?? [];
        list.push({
          name: product.name,
          description: product.description,
          link: product.link,
          month: product.month,
          sections: [group.section, ...product.alsoSections],
        });
        byMonth.set(product.month, list);
      }),
    ),
  );
  return byMonth;
}

// ─────────────────────────── Top Priority ───────────────────────────
//
// The handful of products to start with, drawn from the SAME collection the
// by-system table renders — not a second opinion about the test, just the one
// list narrowed.
//
// IT FILLS TO TOP_PRIORITY_COUNT whenever the test has that many products. A
// shortlist whose length moved with the test read as broken rather than as
// informative: five rows under a heading promising seven is a bug on its face,
// and nothing on screen explained which of the two numbers was wrong.
//
// TWO KINDS OF SLOT, and the split between them is the whole design:
//
//   1. COVERAGE. Every system the Results Overview marks Priority or Support
//      gets its own first choice, most concerning system first. That is the
//      report's own definition of an area to review, and it runs before
//      anything is weighed by citations.
//   1b. PRIORITY MARKERS. A section whose patterns are one marker each may then
//      claim a few more slots for its OTHER Priority findings — the case one
//      slot per system cannot serve. Still coverage: chosen on severity, not on
//      citations. See the pass itself for why it exists and why Priority only.
//   2. DEPTH. Whatever slots are left go to the products the MOST PATTERNS
//      point at, from any system, whether or not that system already has one.
//
// A system's "first choice" is the product its own patterns named first, which
// is the clinical priority; a system whose first choice a worse system already
// claimed contributes its next one.
//
// Citation count is the closest thing this data has to strength of signal, but
// it answers "how widely is this useful", not "how badly does this client need
// it" — which is why it cannot be allowed to decide pass 1. Once every
// concerning system is represented it is deciding depth rather than coverage,
// and that is a question it can answer. Letting it run the whole list produced
// a shortlist with nothing at all from the worst system on it.
//
// Note what pass 2 does NOT guarantee: a system sitting at Monitor gets a slot
// only if one of its products is well cited. That is deliberate — it is not an
// area to review, and the line under the table counts it.
//
// THE CAP IS ELASTIC, copied from the Top Areas band's reasoning: a fixed
// ceiling that cuts a Priority system is cutting the one thing the list exists
// to surface. So it holds at TOP_PRIORITY_COUNT and stretches to
// TOP_PRIORITY_MAX when Priority systems alone would overflow it.
//
// AND WHATEVER IS CUT IS NAMED. unrepresentedSystemCount counts the systems
// with recommendations that got no slot at all, and the view prints it. A
// practitioner who sees Thyroid flagged and no thyroid product here otherwise
// has no way to tell whether one exists — which is the real failure of any cap,
// not the cap.
export const TOP_PRIORITY_COUNT = 7;
export const TOP_PRIORITY_MAX = 10;

// How many EXTRA slots the priority-marker pass may hand a single-marker
// section, on top of the one every recommending system already gets. Two, so
// Vitamins & Minerals tops out at three of the seven and the systems the list
// exists to surface keep the rest.
export const SINGLE_MARKER_EXTRA_SLOTS = 2;

// Which status tiers are served first. Priority and Support — the same two the
// Results Overview's Top Areas band admits.
const SHORTLIST_TIERS: Status[] = ['red', 'orange'];

// SECTIONS WHOSE PATTERNS ARE ONE MARKER EACH.
//
// Vitamins & Minerals is the only one today: every block in its tabs is about a
// single nutrient, so "how many patterns cite this product" is always 1 and
// says nothing. Those sections get the priority-marker pass below, which ranks
// on the marker's own Results Overview status instead.
//
// A list rather than a check against Section.NUTRIENTS, because Electrolytes
// and Hydration are the same shape once their Insights panels open. It stays a
// list of TWO things though — a section and the function that can state its
// per-pattern status — because that second half is what makes the pass
// possible, and no other section can supply it yet. A multi-marker pattern has
// no single marker whose status could stand for it.
const SINGLE_MARKER_SECTIONS: {
  section: Section;
  statuses: (
    categories: { rootCauseCategory: RootCauseCategory }[],
    bloodworkAnalysis: BloodworkAnalysis,
  ) => Map<number, Status>;
}[] = [{ section: Section.NUTRIENTS, statuses: nutrientCategoryStatuses }];

// The pattern names in each single-marker section whose own marker sits at
// Priority.
//
// NAMES, not category ids, because a SectionGroup's legend carries names and
// the legend is how a product says which patterns cite it — patternRefs index
// into it. Going back to ids would mean threading the category through
// collectByBrandAndSection, which nothing else needs.
function priorityMarkerPatterns(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<Section, Set<string>> {
  const bySection = new Map<Section, Set<string>>();
  SINGLE_MARKER_SECTIONS.forEach(({ section, statuses }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    if (!categories.length) return;
    const statusByCategory = statuses(categories, bloodworkAnalysis);
    const names = new Set<string>();
    categories.forEach(category => {
      if (statusByCategory.get(Number(category.rootCauseCategory)) !== 'red') {
        return;
      }
      furtherConsiderations
        .findConsiderations(
          category.rootCauseCategory,
          section,
          'Supplements',
          brands,
        )
        .forEach(consideration => {
          const name = patternNameFromTitle(consideration.title);
          if (name) names.add(name);
        });
    });
    if (names.size) bySection.set(section, names);
  });
  return bySection;
}

// One shortlisted product. Flattened rather than a ProductRow, because a
// ProductRow's `patternRefs` and `alsoSections` are relative to the system card
// it was rendered in and this view has no system cards to be relative to.
export type TopPriority = {
  name: string;
  description: string;
  link?: string;
  kit: KitRef | null;
  month: number;
  // Worst-first: the system that shortlisted it, then the others that also
  // asked for it.
  sections: Section[];
  // Every distinct pattern citing this product across every system — what the
  // Why column counts and the legend spells out.
  patterns: string[];
  // WHICH PASS PUT IT HERE. 'coverage' was chosen on how concerning the finding
  // is — a system's own first choice, or a single-marker section's other
  // Priority markers; 'depth' is a leftover slot, chosen on citations.
  //
  // Two values, not three, even though three passes fill the list. The
  // distinction the table has to draw is what the row was RANKED ON, and the
  // priority-marker rows are ranked on severity exactly like the first-choice
  // rows above them. Splitting them out would put a second divider in the
  // table to describe a difference the reader has no use for.
  //
  // Carried on the row rather than left as a count, because the table has to
  // draw a line between the two and the PDF slices the rows across pages — a
  // boundary index would be wrong the moment a slice starts partway down.
  pass: 'coverage' | 'depth';
};

const TOP_PRIORITY_CACHE = new WeakMap<
  object,
  WeakMap<object, Map<string, TopPriority[]>>
>();

export function collectTopPriorities(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): TopPriority[] {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return [];
  // Memoized for the same reason scopedGroups is: three of the panels on screen
  // ask for the shortlist on every render, and they are asking one question.
  return memoized(
    TOP_PRIORITY_CACHE,
    bloodworkAnalysis,
    furtherConsiderations,
    (brands ?? ['*']).join(','),
    () => computeTopPriorities(bloodworkAnalysis, furtherConsiderations, brands),
  );
}

function computeTopPriorities(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): TopPriority[] {
  // listInEverySystem, so a system's own first choice really is ITS first
  // choice. Under "once each" a product is dropped from every system but the
  // most concerning one, which would silently promote each of those systems'
  // second and third choices into the representative slot.
  const sections: SectionGroup[] = [];
  scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
    'systemicFormulas',
    true,
  ).forEach(group => sections.push(...group.sections));
  const groupBySection = new Map(sections.map(g => [g.section, g] as const));

  // The pattern census, taken over EVERY listing rather than one system's. A
  // product's fourth citation counts even when it arrives from a system whose
  // own first choice it isn't: the question the fill answers is "how many
  // patterns point here", full stop.
  const patternsByProduct = new Map<string, string[]>();
  sections.forEach(group =>
    group.products.forEach(product => {
      const key = normalizeSupplementName(product.name);
      const named = patternsByProduct.get(key) ?? [];
      product.patternRefs.forEach(ref => {
        // patternRefs are 1-based indices into this system's own legend.
        const pattern = group.patterns[ref - 1];
        if (pattern && !named.includes(pattern)) named.push(pattern);
      });
      patternsByProduct.set(key, named);
    }),
  );

  // Systems that recommend something, worst-first, carrying their tier. A
  // system the overview cannot rank (no valued markers, which in practice means
  // no pattern could have fired) falls back to the by-system order, which is
  // that same ranking with the tie-breaks unavailable.
  const ranked = rankSectionsWithStatus(bloodworkAnalysis).filter(entry =>
    groupBySection.has(entry.section),
  );
  const order: Section[] = ranked.length
    ? ranked.map(entry => entry.section)
    : sections.map(group => group.section);
  const severityRank = new Map(order.map((section, i) => [section, i] as const));

  // Elastic ceiling: it stretches rather than cut a Priority system.
  //
  // Stretch TO the number of Priority systems, not straight to the maximum: an
  // eighth Priority system needs an eighth slot, not three. And clamp, because
  // beyond TOP_PRIORITY_MAX a Priority system does get cut — the guarantee is
  // "the cap yields where it can", not "the list has no ceiling".
  const priorityCount = ranked.filter(entry => entry.status === 'red').length;
  const cap = Math.min(
    Math.max(TOP_PRIORITY_COUNT, priorityCount),
    TOP_PRIORITY_MAX,
  );

  const claimed = new Set<string>();
  const take = (
    section: Section,
    row: ProductRow,
    pass: 'coverage' | 'depth',
  ): TopPriority => {
    const key = normalizeSupplementName(row.name);
    claimed.add(key);
    return {
      name: row.name,
      description: row.description,
      link: row.link,
      kit: row.kit,
      month: row.month,
      sections: [section, ...row.alsoSections],
      patterns: patternsByProduct.get(key) ?? [],
      pass,
    };
  };

  // PASS 1 — every system the overview marks Priority or Support gets its own
  // first choice, worst system first. This runs before anything is weighed by
  // citations, which is the whole guarantee: a concerning system's product
  // cannot be crowded out by a product that happens to be widely cited.
  const chosen: TopPriority[] = [];
  order.forEach(section => {
    if (chosen.length >= cap) return;
    const entry = ranked.find(e => e.section === section);
    if (entry && !SHORTLIST_TIERS.includes(entry.status)) return;
    const group = groupBySection.get(section)!;
    // Its first product not already standing for a worse system.
    const row = group.products.find(
      product => !claimed.has(normalizeSupplementName(product.name)),
    );
    if (row) chosen.push(take(section, row, 'coverage'));
  });

  // PASS 1b — THE PRIORITY-MARKER PASS.
  //
  // A section whose patterns are one marker each gets ONE slot out of pass 1
  // like everybody else, and then cannot win another: pass 2 ranks on citation
  // count, and a single-marker product cites exactly one pattern against a
  // glucose or inflammation product's three or four. A client deeply low in
  // both vitamin D and magnesium therefore got D and never magnesium — not
  // because magnesium mattered less, but because the depth key measures breadth
  // of usefulness and a single-marker section has no breadth to show.
  //
  // So those sections may claim up to SINGLE_MARKER_EXTRA_SLOTS more, on the
  // one signal they do have: the marker's own Results Overview status.
  //
  // PRIORITY ONLY. Support is where most nutrients sit on a typical panel, and
  // admitting it would routinely fill the shortlist with them and crowd out the
  // systems this list exists to surface. A Support nutrient keeps its pass 1
  // slot and otherwise takes its chances in pass 2, which is the same deal
  // every other Support finding gets.
  //
  // In the section's own product order, which nutrientCategoryOrder has already
  // sorted worst-marker-first — so the nutrient admitted here is the next worst
  // one, not whichever the sheet happens to list next.
  //
  // THE CAP YIELDS THE SAME WAY it yields for a Priority system. Extras take
  // free slots first, and only stretch past `cap` when pass 1 already filled it
  // — never past TOP_PRIORITY_MAX. Note the knock-on: an extra taken inside the
  // cap costs pass 2 a depth slot, which is the trade the whole pass is making.
  const priorityPatterns = priorityMarkerPatterns(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
  );
  const extrasCeiling = Math.min(
    cap + SINGLE_MARKER_EXTRA_SLOTS,
    TOP_PRIORITY_MAX,
  );
  let extras = 0;
  order.forEach(section => {
    const names = priorityPatterns.get(section);
    const group = groupBySection.get(section);
    if (!names || !group) return;
    group.products.forEach(row => {
      if (extras >= SINGLE_MARKER_EXTRA_SLOTS) return;
      if (chosen.length >= extrasCeiling) return;
      if (claimed.has(normalizeSupplementName(row.name))) return;
      const cited = row.patternRefs.some(ref => names.has(group.patterns[ref - 1]));
      if (!cited) return;
      chosen.push(take(section, row, 'coverage'));
      extras += 1;
    });
  });

  // PASS 2 — the slots left over go to the products the MOST PATTERNS point at,
  // whichever system they came from and whether or not that system already has
  // one. Citation count is the closest thing this data has to strength of
  // signal, and with every concerning system already represented it is now
  // deciding depth rather than coverage, which is the question it can answer.
  //
  // Ties: the more concerning system first, then that system's own priority
  // order, then name so a given test always produces the same list.
  const remaining: { section: Section; row: ProductRow; position: number }[] = [];
  order.forEach(section => {
    groupBySection.get(section)!.products.forEach((row, position) => {
      const key = normalizeSupplementName(row.name);
      if (claimed.has(key)) return;
      if (remaining.some(c => normalizeSupplementName(c.row.name) === key)) return;
      remaining.push({ section, row, position });
    });
  });
  remaining
    .sort(
      (a, b) =>
        (patternsByProduct.get(normalizeSupplementName(b.row.name))?.length ?? 0) -
          (patternsByProduct.get(normalizeSupplementName(a.row.name))?.length ?? 0) ||
        (severityRank.get(a.section) ?? 0) - (severityRank.get(b.section) ?? 0) ||
        a.position - b.position ||
        a.row.name.localeCompare(b.row.name),
    )
    .slice(0, Math.max(0, cap - chosen.length))
    .forEach(candidate =>
      chosen.push(take(candidate.section, candidate.row, 'depth')),
    );

  return chosen;
}

// How many systems with recommendations did NOT get a slot — those sitting
// below Support, and any a full list had no room for. The view prints it, so
// nothing disappears from the shortlist without being counted.
export function unrepresentedSystemCount(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): number {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return 0;
  const shortlisted = new Set(
    collectTopPriorities(bloodworkAnalysis, furtherConsiderations, brands).map(
      product => normalizeSupplementName(product.name),
    ),
  );
  // A system is represented when ANY of its products made the list — not when
  // it OWNS one.
  //
  // Reading the owner alone (product.sections[0]) was the bug: a product is
  // filed under the most concerning system that asked for it, so ImmuneGenic
  // sits under Immune while also being what Vitamins & Minerals recommended.
  // Vitamins & Minerals was then reported as having nothing on the list, on
  // screen, while the row above said "from Immune · Inflammation · Vitamins &
  // Minerals" in plain sight.
  //
  // Checked against the section's own product list rather than the row's
  // "also …" systems, so it holds even if that note is ever narrowed.
  let unrepresented = 0;
  scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
    'systemicFormulas',
  ).forEach(group =>
    group.sections.forEach(section => {
      const covered = section.products.some(product =>
        shortlisted.has(normalizeSupplementName(product.name)),
      );
      if (!covered) unrepresented += 1;
    }),
  );
  return unrepresented;
}

// ───────────────────────── memo ─────────────────────────
//
// COLLECTION IS EXPENSIVE AND IT RUNS A LOT. Every call walks every section,
// every root-cause category under it and every considerations row under that,
// then ranks the sections by severity (which reads every panel's markers). One
// render of the analysis screen asks for it seven times over: the protocols
// panel wants the shortlist and the add-on schedule, whichever view is open
// wants its own groups, and the Recommended Supplements card wants both again.
//
// Nothing about those seven differs, so they are one computation. Without this
// memo, ticking a Client List box re-ran all seven synchronously before React
// could paint, and the tick appeared to lag behind the click by the time the
// card below had rebuilt itself — which is exactly what it was doing.
//
// KEYED ON OBJECT IDENTITY, twice over: a WeakMap on the analysis, then a
// WeakMap on the considerations, then a string for the plain arguments. Both
// models are replaced wholesale when they change (analyze() builds a new
// BloodworkAnalysis), so identity IS the version — and a WeakMap lets a
// superseded analysis be collected with its cache.
//
// What must NOT go stale is the input, not the output: these read root-cause
// categories, considerations rows and marker values. Editing a note or a
// supplement override mutates the same bloodwork but touches none of those, so
// the cached groups stay correct across a tick.
export function memoized<T>(
  store: WeakMap<object, WeakMap<object, Map<string, T>>>,
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  key: string,
  compute: () => T,
): T {
  let byConsiderations = store.get(bloodworkAnalysis as object);
  if (!byConsiderations) {
    byConsiderations = new WeakMap();
    store.set(bloodworkAnalysis as object, byConsiderations);
  }
  let byKey = byConsiderations.get(furtherConsiderations as object);
  if (!byKey) {
    byKey = new Map();
    byConsiderations.set(furtherConsiderations as object, byKey);
  }
  if (byKey.has(key)) return byKey.get(key) as T;
  const value = compute();
  byKey.set(key, value);
  return value;
}

const GROUPS_CACHE = new WeakMap<
  object,
  WeakMap<object, Map<string, BrandGroup[]>>
>();

// collectByBrandAndSection, narrowed to one half of the brand split. Note the
// severity ranking is computed from the whole analysis either way, so a system
// keeps the same position in both halves.
//
// The result is SHARED, not copied — see the memo above. Callers read it; none
// mutates it, and one that started to would be changing what every other panel
// on the page sees.
export function scopedGroups(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
  scope: SupplementsBrandScope,
  listInEverySystem = true,
): BrandGroup[] {
  return memoized(
    GROUPS_CACHE,
    bloodworkAnalysis,
    furtherConsiderations,
    `${scope}|${listInEverySystem}|${(brands ?? ['*']).join(',')}`,
    () =>
      collectByBrandAndSection(
        bloodworkAnalysis,
        furtherConsiderations,
        brands,
        rankSectionsBySeverity(bloodworkAnalysis),
        listInEverySystem,
      ).filter(group => inScope(group.brand, scope)),
  );
}

// Lightweight markdown-ish convention used by the override textarea. The
// rendered view parses these markers back into brand / section / bullet
// styling.
//   "## Brand"            → brand heading (h4)
//   "### Section"          → section heading (h6)
//   "- Name — Description" → bullet with bold name
//   anything else          → plain paragraph
//
// NOTE the generated view is a table while an edited one is a bullet list. That
// divergence is deliberate: an override is a free-text replacement the
// practitioner owns, and a list is what's actually editable by hand — there is
// no sane plain-text syntax for "product, description, systems, kit" that
// someone would want to maintain in a textarea. The seed text below carries the
// system and kit inline so nothing is lost when they start editing.
function groupsToText(groups: BrandGroup[]): string {
  return groups
    .map(({ brand, sections }) => {
      const blocks = sections
        .map(group => {
          const bullets = group.products
            .map(p => {
              const base = `- ${p.name} — ${p.description}`;
              const meta = `[Month ${p.month} · ${kitLabel(p.kit)}]`;
              return p.link ? `${base} ${meta} — ${p.link}` : `${base} ${meta}`;
            })
            .join('\n');
          return `### ${sectionShortLabel(group.section)}\n${bullets}`;
        })
        .join('\n\n');
      return `## ${brandLabel(brand)}\n\n${blocks}`;
    })
    .join('\n\n');
}

function renderOverride(override: string): JSX.Element {
  const lines = override.split('\n');
  const nodes: JSX.Element[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} style={{ marginBottom: 8 }}>
        {bullets.map((b, i) => {
          const supp = parseItem(b);
          return supp ? (
            <li key={i}>
              {renderSupplementName(supp)} — {supp.description}
            </li>
          ) : (
            <li key={i}>{b}</li>
          );
        })}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (line.startsWith('- ')) {
      bullets.push(line.slice(2));
    } else if (trimmed.startsWith('### ')) {
      flushBullets();
      nodes.push(
        <h6
          key={`h6-${idx}`}
          style={{
            marginTop: nodes.length > 0 ? 14 : 0,
            marginBottom: 6,
            marginLeft: 8,
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          {trimmed.slice(4)}
        </h6>,
      );
    } else if (trimmed.startsWith('## ')) {
      flushBullets();
      nodes.push(
        <h4
          key={`h4-${idx}`}
          style={{
            marginTop: nodes.length > 0 ? 18 : 0,
            marginBottom: 12,
            fontSize: '1.5rem',
          }}
        >
          <strong>{trimmed.slice(3)}</strong>
        </h4>,
      );
    } else if (trimmed === '') {
      flushBullets();
    } else {
      flushBullets();
      nodes.push(<div key={`p-${idx}`}>{line}</div>);
    }
  });
  flushBullets();

  return <>{nodes}</>;
}

// ---------------------------------------------------------------------------
// PDF pagination
//
// The PDF renders one Card per page with an explicit page break between them,
// rather than one tall Card that html2pdf slices wherever it lands. See
// pdfSupplementsPagination.ts for why a page-break-inside rule cannot do this
// job. Both content paths (auto-generated and the practitioner's edited
// override) funnel into the same block list so both paginate.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Table rendering — shared by the screen and the PDF so the two can't drift.
// ---------------------------------------------------------------------------

export const CELL: React.CSSProperties = {
  padding: '4px 10px 4px 0',
  borderBottom: '1px solid #e9ecef',
  verticalAlign: 'middle',
};

// Short columns hold their content on one line. No overflow handling: these
// are sized to fit, so there is nothing to clip — only Description wraps.
const NOWRAP: React.CSSProperties = { whiteSpace: 'nowrap' };
export const HEAD_CELL: React.CSSProperties = {
  padding: '0 10px 5px 0',
  borderBottom: '1px solid #ced4da',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#6c757d',
  whiteSpace: 'nowrap',
};

// The hint on the PRODUCT column.
//
// Every product name in these tables is a link to the maker's page, and nothing
// says so until the pointer is already on one — the names are bold and blue,
// which on a dense table reads as emphasis as easily as it reads as a link. The
// mark states it once for the whole column instead of per row.
//
// SCREEN ONLY. It tells the reader to click, and the printed report is a
// picture: html2canvas rasterizes the page, so a link in the PDF is not a link
// and a tooltip has nowhere to appear. Every printed header passes false.
//
// It names Systemic Formulas rather than "the maker", which is vague about a
// destination the reader is being asked to click into. That is safe while
// Systemic Formulas is the only brand these tables can show; the day BWH Labs
// unparks ([[project_second_supplement_brand]]) this has to take the brand from
// the group it heads, because a card of Beta Glucan products would be pointing
// at the wrong site.
export const PRODUCT_LINK_HINT =
  'Click any product name to open its page on the Systemic Formulas website.';

const PRODUCT_HINT_TOOLTIP_ID = 'product-link-hint';

// An OverlayTrigger, NOT a `title` attribute. The native tooltip is on the
// browser's own timer — roughly a second of hovering before it appears — which
// on a mark this small reads as nothing happening at all. The overlay is the
// same tooltip the PRC hover text uses (further-considerations-section.tsx) and
// it opens on the mouse entering.
//
// THE FLICKER, and the three things it took to kill it. A hover tooltip
// oscillates whenever the box it opens can take the pointer off the mark that
// opened it: mouse onto the box, the mark reads that as mouse-out, the box
// closes, the pointer is back on the mark, the box opens. All three of these
// exist to make that impossible.
//
// 1. `container={document.body}`. Bootstrap renders the overlay INLINE, which
//    here means a positioned div inside a <th>: the cell's `white-space:
//    nowrap` stretches the sentence into one long line, and the table's
//    `overflow-x: auto` wrapper clips it — a box with `overflow-x: auto`
//    computes `overflow-y` to `auto` as well, so it clips vertically too, which
//    is the direction this box opens in. Portalled to the body it is measured
//    and placed against the viewport.
// 2. `pointer-events: none` on the tooltip (index.css). The definitive fix: the
//    box cannot receive the pointer at all, so it can never take it off the
//    mark. Bootstrap 4 does not set this on `.tooltip` itself.
// 3. `placement="bottom"`. The heading sits near the top of the panel, so a box
//    placed above it is short of room and popper flips it — and a flip
//    recalculated against a box whose own size changes with placement can
//    oscillate. Downward there is a whole table's worth of room and nothing to
//    decide.
//
// `aria-label` on a role="img" span rather than FontAwesome's own `title`,
// which renders inside the svg where the heading's uppercase letter-spacing
// applies to it as well — the label sat a character-width too far right.
function ProductHeadCell({ withHint }: { withHint: boolean }): JSX.Element {
  return (
    <th style={HEAD_CELL}>
      Product
      {withHint && (
        <OverlayTrigger
          placement="bottom"
          container={document.body}
          overlay={
            <Tooltip id={PRODUCT_HINT_TOOLTIP_ID}>{PRODUCT_LINK_HINT}</Tooltip>
          }
        >
          <span
            role="img"
            aria-label={PRODUCT_LINK_HINT}
            style={{
              // No color of its own: it inherits the heading's #6c757d, which
              // is also the gray the Results Overview's own info marks fall
              // back to, so the two read as the same kind of object. It was the
              // link blue, which made a column heading the most saturated thing
              // on a table whose actual links are the product names under it.
              marginLeft: '5px',
              letterSpacing: 'normal',
            }}
          >
            {/* The size goes ON THE ICON, not on this span. index.css carries a
                blanket `.svg-inline--fa { font-size: 16px }` AND a
                `.fa-info-circle { font-size: 16px }`, both of which sit on the
                svg itself and beat anything the parent hands down — which is
                why two rounds of shrinking the span changed nothing. An inline
                style on the svg is what outranks them. 12px beside the 11px
                heading: a hair over the label so the circle is legible, which
                10px was not. */}
            <FontAwesomeIcon
              icon="info-circle"
              style={{
                fontSize: '12px',
                // Centered on the CAP BAND, not the baseline. The heading is
                // uppercase, so its letters run from the baseline up to about
                // 0.7em and there is no x-height for the icon to sit against.
                // A 12px icon centered on 11px caps drops (12 - 7.7) / 2 =
                // 2.2px below the baseline, which is this. FontAwesome's own
                // -0.125em default is tuned for lowercase text and left the
                // mark riding high; `baseline` sat it higher still.
                verticalAlign: '-0.18em',
              }}
            />
          </span>
        </OverlayTrigger>
      )}
    </th>
  );
}

// The system heading that opens each group, plus the numbered legend of the
// patterns its products cite. Spans the whole table so the legend has room to
// wrap — it's the one place a pattern name is spelled out, so it must not be
// squeezed into a column.
// Column widths, shared by the on-screen grouped table and the PDF's
// per-section slices so the two line up column for column.
// Sized so every column holds its content on one line: Product to the longest
// name, Pattern to its legend numbers, Month to "Month 1 · Holobiome Kit", and
// Description takes everything left over. The Pattern column's "also <system>"
// note is the one thing that doesn't fit beside its numbers, so it prints on
// its own line rather than widening a column the other rows don't need.
//
// THE MONTH COLUMN IS THE ROADMAP'S, and only the Holobiome half ever had one
// to show. A start month is a position in the five-month program; the two list
// views answer "what does this test call for", which is a different question,
// and a month sitting beside a product there invited the reader to treat the
// list as a schedule it isn't. It survives where it means something: the month
// rail and the Targeted Additions boxes on the Roadmap view.
//
// The width it gave up goes to Description, which is the only column whose
// content is a paragraph and the one that was wrapping while its neighbours
// held air.
const COL_WIDTHS = { product: 15, description: 65, pattern: 20 };

const COLGROUP = (
  <colgroup>
    <col style={{ width: `${COL_WIDTHS.product}%` }} />
    <col style={{ width: `${COL_WIDTHS.description}%` }} />
    <col style={{ width: `${COL_WIDTHS.pattern}%` }} />
  </colgroup>
);

// The same table with the client-list box on the end.
//
// Not the four shared widths plus a fifth: those were sized for a table whose
// last column ended at the panel edge, and simply appending to them left
// Recommended Month floating in the middle of a run of white with the tick a
// hand's width away. Month and the tick column are both sized to their content
// here ("Month 2 · Holobiome Kit" and a checkmark), and everything they give
// back goes to Description — the only column whose content is a paragraph, and
// the one that was wrapping to two lines while its neighbours held air.
export const CLIENT_LIST_COL_WIDTHS = {
  product: 13,
  description: 66,
  pattern: 15,
  clientList: 6,
};

const CLIENT_LIST_COLGROUP = (
  <colgroup>
    <col style={{ width: `${CLIENT_LIST_COL_WIDTHS.product}%` }} />
    <col style={{ width: `${CLIENT_LIST_COL_WIDTHS.description}%` }} />
    <col style={{ width: `${CLIENT_LIST_COL_WIDTHS.pattern}%` }} />
    <col style={{ width: `${CLIENT_LIST_COL_WIDTHS.clientList}%` }} />
  </colgroup>
);

// 12px matches the Add-On Products table in the Protocols panel. The two are
// the same kind of object — a dense list of products with a description each —
// so they read as one system rather than two tables that happen to sit in the
// same report. Every body cell inherits this; only the section heading and its
// legend sit above it.
const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: '12px',
};

// The three column headings. Shared so the copy that opens each system on
// screen and the one at the top of each PDF slice can't drift apart. The
// argument is the one thing the two do NOT share — see PRODUCT_LINK_HINT.
const headerCells = (withProductHint: boolean): JSX.Element => (
  <>
    <ProductHeadCell withHint={withProductHint} />
    <th style={HEAD_CELL}>Description</th>
    <th style={{ ...HEAD_CELL, textAlign: 'center' }}>Pattern</th>
  </>
);

// The numbered legend of a system's patterns — the one place a pattern name is
// spelled out. Everything else cites it by number, which is what stops a
// 50-character name repeating on four rows.
//
// It opens with the word "Pattern", because a row of bare numerals does not say
// what it is. The Pattern column on the right holds the same numerals, and
// until the legend named itself the reader had to infer the link between the
// two from their both being small and numeric.
// How many patterns sit side by side. TWO, not "as many as fit".
//
// The legend was briefly a flex-wrap of `1 1 320px` entries, which on a wide
// panel gave four columns of wildly uneven length — a fourteen-word pattern
// beside a two-word one, each row a different shape — and the eye had nothing
// to follow. Two columns is what makes the names long enough to sit on one line
// most of the time, and a wrapped one stays inside its own cell.
const LEGEND_COLUMNS = 2;
// Fixed widths, so every rule lines up whatever the names do. `tableLayout:
// fixed` is what makes this hold: without it the browser sizes columns to their
// content and the borders go ragged, which was the objection to bordering it at
// all.
//
// The pair does NOT split the width evenly. Two 50% halves put the right-hand
// column's number at the panel's midpoint, which on a wide report is a hand's
// width of nothing after most names — the names are short and the column was
// sized for the longest. 42% each leaves the longest name on one line and pulls
// the second column in; the slack is parked in a trailing filler column, where
// it reads as a margin rather than as a gap inside the list.
// 44, raised from 36 on 2026-08-31. Pulling the columns in to close the gap
// after the SHORT names was the wrong trade: it cost the long ones a second
// line each, and it parked a fifth of the panel (20%) in the filler, where on a
// full-width report it read as an empty right-hand margin rather than as slack.
// Measured on the fifteen patterns of a full metabolic report at the PDF's
// 988px page width: 36% wraps two rows, 38% is the first width where none wrap,
// and 44% leaves the longest name in the report ("Elevated Cortisol May Be
// Driving Glucose Output and Dysregulation") on one line with room to spare.
// That leaves a 4% filler, which is a margin. The gap after a short name is
// what the rule under every entry is there to carry the eye across.
const LEGEND_NUMBER_WIDTH = 4;
const LEGEND_NAME_WIDTH = 44;
// What is left over at the right edge. At two columns it falls out of the name
// width above; at one it is set directly, so a lone pattern keeps the same
// right-hand margin as a full list rather than running to the panel edge.
const LEGEND_FILLER_WIDTH_MIN =
  100 - LEGEND_COLUMNS * (LEGEND_NUMBER_WIDTH + LEGEND_NAME_WIDTH);

// The numbered patterns a group's products cite — the one place a pattern is
// spelled out. Everything else refers to it by number.
//
// A REAL TABLE, with a rule under every entry. As running text behind a
// "Patterns:" label it was fine for the two or three a single system cites and
// unreadable for the fourteen the Top Priority view collects across the whole
// report: numbers and names on one baseline, wrapping wherever the panel ended,
// so finding number 9 meant reading the paragraph. The rules give the eye a
// line to travel and, more to the point, separate an entry that wraps from the
// one under it.
export function PatternLegend({
  patterns,
}: {
  patterns: string[];
}): JSX.Element | null {
  if (patterns.length === 0) return null;
  /*
   * A LONE PATTERN TAKES THE WHOLE WIDTH. Two columns exist to stop a list of
   * fourteen running down one side of the panel; with one entry there is no
   * list, and holding it to half the width wrapped Stress & Adrenals' single
   * "Cortisol-Dominant with Low Resilience & Low Mineral Balance" onto a second
   * line against an empty right half.
   */
  const columns = patterns.length === 1 ? 1 : LEGEND_COLUMNS;
  const nameWidth =
    columns === 1
      ? 100 - LEGEND_NUMBER_WIDTH - LEGEND_FILLER_WIDTH_MIN
      : LEGEND_NAME_WIDTH;
  const fillerWidth = 100 - columns * (LEGEND_NUMBER_WIDTH + nameWidth);

  // COLUMN-MAJOR: 1–7 down the left, 8–14 down the right. Filling row by row
  // instead put 1 and 2 side by side, which means reading a numbered list in
  // boustrophedon — the eye has to cross the panel and come back for every
  // increment. Down one column and on to the next is how a numbered list is
  // read everywhere else.
  const perColumn = Math.ceil(patterns.length / columns);
  const rowIndexes = Array.from({ length: perColumn }, (_, row) =>
    Array.from({ length: columns }, (_, col) => col * perColumn + row),
  );
  return (
    // The PRODUCT ROWS' own size (TABLE_STYLE's 12px), not a step above it.
    // The legend used to be set at 13px on the argument that this is the only
    // place a pattern is named, so it had to be comfortably readable. At
    // fourteen entries that argument stopped paying: a block set larger than
    // the products it annotates, running four lines deep before the table
    // starts, reads as the main event. Matching the descriptions puts it back
    // in its place and buys the rows the space.
    <div style={{ fontSize: '12px', color: '#6c757d' }}>
      {/* Singular when there is one to name — "Patterns" over a list of one
          would be advertising a list that isn't there. */}
      <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: 2 }}>
        {patterns.length === 1 ? 'Pattern' : 'Patterns'}
      </div>
      <table
        style={{
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
          fontSize: '12px',
        }}
      >
        <colgroup>
          {Array.from({ length: columns }).map((_, i) => (
            <React.Fragment key={i}>
              <col style={{ width: `${LEGEND_NUMBER_WIDTH}%` }} />
              <col style={{ width: `${nameWidth}%` }} />
            </React.Fragment>
          ))}
          <col style={{ width: `${fillerWidth}%` }} />
        </colgroup>
        <tbody>
          {rowIndexes.map((row, rowIdx) => {
            // The last row closes the legend, and a rule immediately above the
            // products under it would read as a double line. Same treatment
            // every table in this section gives its final row.
            const cell: React.CSSProperties = {
              padding: '3px 10px 3px 0',
              borderBottom:
                rowIdx === rowIndexes.length - 1 ? 'none' : '1px solid #eef0f2',
              verticalAlign: 'top',
              lineHeight: 1.35,
            };
            return (
              <tr key={patterns[row[0]]}>
                {row.map((index, col) => {
                  const pattern = patterns[index];
                  return (
                    <React.Fragment key={col}>
                      {/* Right-aligned and tabular so 1 and 14 end on the same
                          column and every name starts on the next one. */}
                      <td
                        style={{
                          ...cell,
                          textAlign: 'right',
                          paddingRight: 7,
                          fontWeight: 'bold',
                          color: '#495057',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {pattern ? index + 1 : ''}
                      </td>
                      <td style={cell}>{pattern ?? ''}</td>
                    </React.Fragment>
                  );
                })}
                {/* Slack column — see LEGEND_FILLER_WIDTH. */}
                <td style={cell} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// A two-or-more segment switch, in the neutral the Holobiome panel reserves for
// chrome. Shared by the Roadmap / By System toggle in supplement-protocols-panel
// and the listing toggle below, so the panel's two switches can't drift into
// looking like different kinds of control.
//
// NEUTRAL, not a brand color, and deliberately: on this panel a blue says "part
// of the Holobiome roadmap", and a view switcher is chrome. It also leaves the
// section with exactly one colored button, which is the one that asks for the
// sale.
export const TOGGLE_ACTIVE = '#495057';

export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        border: '1px solid #ced4da',
        borderRadius: '5px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
      }}
    >
      {options.map((option, i) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            style={{
              appearance: 'none',
              border: 0,
              borderRight: i < options.length - 1 ? '1px solid #ced4da' : 0,
              backgroundColor: active ? TOGGLE_ACTIVE : 'transparent',
              color: active ? '#fff' : TOGGLE_ACTIVE,
              fontWeight: active ? 600 : 'normal',
              fontSize: '12.5px',
              padding: '5px 14px',
              cursor: 'pointer',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// The listing choice on the by-system table: "In every system" or "Once each".
//
// NOT a segmented pill, though it started as one. The panel's Roadmap / By
// System switch is a filled dark pill, and a second pill under it read as its
// equal — two controls of the same weight, 40px apart. They are not equals:
// one changes WHAT you are looking at, this one only changes how a single
// table enumerates itself. So it is a different class of control, not a
// smaller version of the same one, and the card keeps exactly one filled pill.
//
// The chosen state is bold and dark and the alternative is the only blue thing
// in the strip, so the pair reads as "this, or that instead". Both are buttons
// so a keyboard user gets the same two targets a mouse does; clicking the
// active one is simply a no-op.
export function ListingChoice<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        fontSize: '11.5px',
        color: '#6c757d',
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
        paddingTop: '2px',
      }}
    >
      {label}:{' '}
      {options.map((option, i) => {
        const active = option.value === value;
        return (
          <React.Fragment key={option.value}>
            {i > 0 && <span style={{ color: '#adb5bd' }}> · </span>}
            <button
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              style={{
                appearance: 'none',
                border: 0,
                padding: 0,
                background: 'none',
                fontSize: '11.5px',
                fontWeight: active ? 'bold' : 'normal',
                color: active ? '#212529' : HOLOBIOME_ANCHOR,
                textDecoration: active ? 'none' : 'underline',
                cursor: active ? 'default' : 'pointer',
              }}
            >
              {option.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// The system heading that opens each group. Plain black and bold rather than a
// jump link: the products under it are what the reader is here for, and a blue
// underlined heading competed with the product names — which ARE links — for
// the same "click me" signal.
function SectionHeading({ section }: { section: Section }): JSX.Element {
  return (
    <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#212529' }}>
      {sectionFullLabel(section)}
    </span>
  );
}

// The practitioner's client-list selection, as the tables see it: a product is
// on the list or it isn't, and clicking flips it. Deliberately not a Set prop —
// selection is stored inverted (the note holds what was UNticked, so a newly
// recommended product is on the list by default) and only the owner should know
// that. See recommended-supplements-panel.tsx.
export type ClientListSelection = {
  isSelected: (productName: string) => boolean;
  toggle: (productName: string) => void;
};

// One product row. Shared so the screen and the PDF can't drift.
//
// Description WRAPS. It was briefly clipped to an ellipsis to force every row
// onto one line, but catalog descriptions run to 230 characters — clipping hid
// most of the text behind a hover, which is the opposite of readable. The
// short columns stay on one line; the description sets the row height.
// `isLast` drops the row's bottom rule. Every CELL carries one as the row
// separator, which means the final row closes the table with a line — and in
// the Holobiome panel the footer draws its own rule 16px below that, so the
// section ended in two parallel lines. A table should not put a separator after
// the last thing it separates. Defaults to false, so the PDF slices (where each
// block genuinely does close) are unchanged.
function productRow(
  p: ProductRow,
  isLast = false,
  clientList?: ClientListSelection,
  onSectionClick?: (section: Section) => void,
): JSX.Element {
  const cell = isLast ? { ...CELL, borderBottom: 'none' } : CELL;
  return (
    <tr key={p.name}>
      {/* No NOWRAP here, unlike the other short columns. At 12% the widest
          name ("Metabolic Superfood") only just fits, and on a narrow window it
          wouldn't — a fixed-layout cell that can't wrap overflows into its
          neighbour rather than clipping, so wrapping is the safe degradation. */}
      <td style={cell}>{renderSupplementName(p)}</td>
      <td style={cell}>{p.description}</td>
      {/* Numbers into the legend above, not the pattern names — several of a
          system's products share one 50-character pattern, and spelling it out
          per row is what made this redundant. */}
      <td style={{ ...cell, ...NOWRAP, textAlign: 'center' }}>
        {p.patternRefs.join(', ')}
        {/* Own line, not " · also Glucose" trailing the numbers: at this
            column's 12% the inline form ran out of the cell and collided with
            "Month 1" next door. A block drops it under the numbers, and
            whiteSpace normal lets a product claimed by several systems wrap
            down instead of overflowing again. */}
        {p.alsoSections.length > 0 && (
          <div style={{ color: '#8b96a3', whiteSpace: 'normal' }}>
            also{' '}
            {/* Each system inline-block with its own leading separator, so a
                list too long for the column wraps as "also Glucose / ·
                Digestion" rather than stranding the dot at the line end. */}
            {p.alsoSections.map((section, i) => (
              <span
                key={section}
                style={{ display: 'inline-block', marginRight: 4 }}
              >
                {i > 0 && '· '}
                <SectionJump
                  section={section}
                  label={sectionShortLabel(section)}
                  onSectionClick={onSectionClick}
                />
              </span>
            ))}
          </div>
        )}
      </td>
      {/* The client-list box. Screen only — the printed slices have no column
          for it, because a checkbox on paper is a decision nobody can make.
          Labeled per product rather than by column position, so a screen
          reader hears what it is about to add rather than "checkbox, row 4". */}
      {clientList && (
        <td style={{ ...cell, textAlign: 'center' }}>
          <input
            type="checkbox"
            className="lsm-styled-checkbox lsm-styled-checkbox--on-light"
            checked={clientList.isSelected(p.name)}
            onChange={() => clientList.toggle(p.name)}
            aria-label={`Include ${p.name} in the client's recommended supplements`}
            title={`Include ${p.name} in the client's recommended supplements`}
          />
        </td>
      )}
    </tr>
  );
}

// On-screen table: every system in one table, each opening with a heading row
// that carries its link and its pattern legend.
function ProductTable({ sections }: { sections: SectionGroup[] }): JSX.Element {
  return (
    // Horizontal scroll on the table's own container, so a narrow viewport
    // never makes the whole report scroll sideways.
    <div style={{ overflowX: 'auto' }}>
      {/* Percentage widths on a fixed layout: the table fills the panel, but
          Description can't run away with the slack the way it did under auto
          layout — its share is capped, and Pattern stays narrow because its
          content is short and fixed-shape. */}
      <table style={TABLE_STYLE}>
        {COLGROUP}
        {/* No <thead>: the column headings repeat inside each system instead,
            below that system's name and legend. A single set at the top of the
            table sat above the first system's heading, which read as though the
            heading were data under it. */}
        <tbody>
          {sections.map((group, groupIdx) => (
            <React.Fragment key={String(group.section)}>
              <tr>
                <td
                  colSpan={3}
                  style={{
                    borderBottom: 'none',
                    paddingTop: groupIdx === 0 ? 0 : 26,
                    paddingBottom: 8,
                  }}
                >
                  <SectionHeading section={group.section} />
                  <PatternLegend patterns={group.patterns} />
                </td>
              </tr>
              <tr>{headerCells(true)}</tr>
              {group.products.map((product, productIdx) =>
                productRow(
                  product,
                  groupIdx === sections.length - 1 &&
                    productIdx === group.products.length - 1,
                ),
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// DOSING IS NOT HERE, and deliberately.
//
// It used to close the by-system list on screen and in the PDF: a two-column
// table of servings BY FORM ("All capsule products", "Superfood (powder)"),
// which left the reader to work out which form each product came in and then
// find its row. The Recommended Supplements card now prints the serving on the
// product's own line, resolved by doseForProduct off the same HOLOBIOME_DOSING
// rows — the answer instead of the lookup. Two statements of one fact is how
// they drift, so this half states none.
//
// HOLOBIOME_DOSING stays where it is: it is still the source doseForProduct
// reads. What went is the table that made the practitioner do the join.


// The by-system view of the Holobiome Roadmap: ONE CARD PER SYSTEM, in the
// report's own section chrome — the blue header every section panel above it
// wears, and a white body holding that system's legend and products.
//
// Why a card rather than the bold heading ProductTable uses. This view answers
// "what does this blood work call for", and the answer is read one system at a
// time: a practitioner looking up Glucose wants that system's block, not a
// place in a long table. The blue header is the same object the rest of the
// report already uses to say "this is a system", so a reader arrives here
// knowing what the boxes are. The Supplements panel keeps the flat table — it
// is a single continuous list of everything, and cards there would chop a list
// nobody reads by system into boxes.
//
// Every column measurement is shared with that flat table (COLGROUP,
// headerCells, productRow), so the two views state a product identically and
// the columns line up card to card.
function SystemCard({
  group,
  clientList,
  onSectionClick,
}: {
  group: SectionGroup;
  clientList?: ClientListSelection;
  onSectionClick?: (section: Section) => void;
}): JSX.Element {
  return (
    // summary-section-card tightens .card-body to 8px (index.css) — the same
    // class the Outside Optimal panels use, so a card inside a panel is not as
    // roomy as a top-level section card. The header is left alone: no color
    // override means it paints the report's own blue gradient, which is the
    // whole point of putting a card here.
    <Card className="summary-section-card">
      {/* THE WHOLE HEADER IS THE JUMP TARGET, not the run of text inside it.
          It is already the report's own section chrome and already carries the
          system's name, so a reader who wants that system's panel has nowhere
          more obvious to click — and a bar that responds only where the letters
          happen to fall is a target you have to aim at. The handler is on the
          header itself rather than on a child, so the padding either side of
          the title is live too.
          The class only adds a cursor, a hover underline and a focus ring; the
          header keeps index.css's gradient and its white text. */}
      <Card.Header
        {...sectionJumpProps(
          group.section,
          sectionFullLabel(group.section),
          onSectionClick,
          true,
        )}
      >
        {sectionFullLabel(group.section)}
        {onSectionClick && (
          <FontAwesomeIcon
            icon="link"
            className="lsm-section-jump__icon"
            aria-hidden
          />
        )}
      </Card.Header>
      <Card.Body>
        {group.patterns.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <PatternLegend patterns={group.patterns} />
          </div>
        )}
        {/* Horizontal scroll on the card, so a narrow viewport never makes the
            whole report scroll sideways. */}
        <div style={{ overflowX: 'auto' }}>
          <table style={TABLE_STYLE}>
            {/* Its own colgroup when the client-list box is on: the four
                shared widths are re-proportioned rather than squeezed, so
                Description gives up the space instead of Product wrapping. */}
            {clientList ? CLIENT_LIST_COLGROUP : COLGROUP}
            {/* A thead per card, unlike ProductTable's heading rows: each card
                is its own table, so the columns need naming inside each one. */}
            <thead>
              <tr>
                {headerCells(true)}
                {clientList && (
                  <th style={{ ...HEAD_CELL, textAlign: 'center' }}>
                    Client List
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {group.products.map((product, productIdx) =>
                // The last row of EVERY card drops its separator, not just the
                // last card's: a rule immediately above the card's own border
                // reads as a double line. Same reasoning as ProductTable's
                // final row, applied per box.
                productRow(
                  product,
                  productIdx === group.products.length - 1,
                  clientList,
                  onSectionClick,
                ),
              )}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );
}

// PDF slice: the products of ONE system, with column headers but no group
// heading — the block's own label carries the system name, and repeats with
// "(cont.)" when the packer splits it across pages.
function PdfProductTable({ products }: { products: ProductRow[] }): JSX.Element {
  return (
    <table style={TABLE_STYLE}>
      {COLGROUP}
      <thead>
        <tr>{headerCells(false)}</tr>
      </thead>
      {/* Not `.map(productRow)` — map would pass the index into `isLast`. A PDF
          slice keeps its closing rule either way: it ends a printed block, with
          no footer rule beneath it to double up against. */}
      <tbody>{products.map(product => productRow(product))}</tbody>
    </table>
  );
}

// A SECTION NAME THAT JUMPS TO ITS SECTION.
//
// Same shape as the roadmap's SectionOrigins (supplement-protocols-panel):
// role=button on a span rather than an <a>, because there is no URL — the
// handler expands the section if it's collapsed and then scrolls, which an href
// cannot do. Keyboard gets Enter and Space, so the link is reachable without a
// mouse.
//
// WITHOUT `onSectionClick` it renders as plain text, and that is what the PDF
// gets: a jump link on paper is an underline that does nothing. Both the print
// path and the other-brands Supplements panel simply don't pass the handler.
//
// TWO DRESSES. A card header takes `marked`: a link icon after the title, and
// the underline only on hover — a whole header underlined edge to edge read as
// a rendering fault. An inline origin ("from Immune · Glucose") takes the
// default: underlined at rest like any other link, and no icon, because three
// gray words in a table cell need no explaining and an icon after each would
// litter the cell. See .lsm-section-jump in App.css, which also records why the
// mark is a chain link and not an arrow of any kind.
//
// Styling lives in the stylesheet rather than inline because :hover and
// :focus-visible cannot be expressed as inline styles.
// The interactive half, shared by the inline links and the card header so the
// keyboard handling can't diverge between them. Returns nothing when there is
// no handler, which is what leaves the PDF with plain text.
export function sectionJumpProps(
  section: Section,
  label: string,
  onSectionClick?: (section: Section) => void,
  marked = false,
): React.HTMLAttributes<HTMLElement> {
  if (!onSectionClick) return {};
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onSectionClick(section),
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSectionClick(section);
      }
    },
    title: `Go to ${label}`,
    className: `lsm-section-jump lsm-section-jump--${
      marked ? 'marked' : 'inline'
    }`,
  };
}

export function SectionJump({
  section,
  label,
  onSectionClick,
  marked = false,
  style,
}: {
  section: Section;
  label: string;
  onSectionClick?: (section: Section) => void;
  // Link icon instead of a standing underline. For the card headers only.
  marked?: boolean;
  style?: React.CSSProperties;
}): JSX.Element {
  if (!onSectionClick) return <span style={style}>{label}</span>;
  return (
    <span
      {...sectionJumpProps(section, label, onSectionClick, marked)}
      style={style}
    >
      {label}
      {marked && (
        // aria-hidden: the span already reads out the section's name, and its
        // title says where the link goes. A second announcement of "link" is
        // noise.
        <FontAwesomeIcon
          icon="link"
          className="lsm-section-jump__icon"
          aria-hidden
        />
      )}
    </span>
  );
}

// A product row (generated view), a bullet, or a stray line of prose. The last
// two only come from an edited override, which stays a list — see groupsToText.
type PdfItem = {
  key: string;
  row?: ProductRow;
  // The numbered pattern legend that opens a system's slice.
  legend?: string[];
  bullet?: JSX.Element;
  text?: string;
};

// Height estimates in html2pdf canvas px, measured off a rendered report at the
// PDF's ~800px content width. Pattern headings and product bullets both wrap to
// two lines more often than not, so these are sized for the two-line case:
// overestimating only costs a little whitespace, while underestimating puts
// content back in the footer.
const PDF_PAGE_CAPACITY = 900;
const PDF_CARD_OVERHEAD = 76;
const PDF_BRAND_HEADING = 48;
const PDF_PATTERN_HEADING = 62;
// A table row now carries a pattern name under its system as well as the
// description, so it runs taller than the old single-line bullet.
const PDF_ITEM_HEIGHT = 46;

// One block per brand, holding every product row. The packer splits a block
// that overflows a page and flags the continuation, so a long list breaks
// between rows and repeats its header — the same treatment the old per-pattern
// blocks got, minus the pattern headings that no longer exist.
// One block per (brand, system). The packer splits a system that overflows a
// page and repeats its label with "(cont.)", so a long list breaks between
// products under a heading that still says which system they belong to.
//
// `showBrandHeadings` is off for the single-brand Holobiome scope, where the
// card header already names the brand.
function blocksFromGroups(
  groups: BrandGroup[],
  showBrandHeadings: boolean,
): SupplementsBlock<PdfItem>[] {
  const blocks: SupplementsBlock<PdfItem>[] = [];
  groups.forEach(({ brand, sections }) => {
    sections.forEach((group, idx) => {
      blocks.push({
        key: `${brand}-${group.section}`,
        // Charged to the brand's first system only, so the brand heading
        // travels with it and can't be stranded at the foot of a page.
        brandLabel: idx === 0 && showBrandHeadings ? brandLabel(brand) : null,
        label: sectionShortLabel(group.section),
        items: [
          ...(group.patterns.length > 0
            ? [{ key: `${group.section}-legend`, legend: group.patterns }]
            : []),
          ...group.products.map(p => ({ key: p.name, row: p })),
        ],
        isContinuation: false,
      });
    });
  });
  return blocks;
}

// Same markdown-ish convention renderOverride parses, mapped onto blocks so an
// edited panel paginates exactly like a generated one. A brand heading with no
// pattern under it, or bullets with no heading, still produce a valid block.
function blocksFromOverride(override: string): SupplementsBlock<PdfItem>[] {
  const blocks: SupplementsBlock<PdfItem>[] = [];
  let pendingBrand: string | null = null;
  let current: SupplementsBlock<PdfItem> | null = null;

  const open = (label: string | null, key: string) => {
    current = {
      key,
      brandLabel: pendingBrand,
      label,
      items: [],
      isContinuation: false,
    };
    pendingBrand = null;
    blocks.push(current);
  };

  override.split('\n').forEach((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      // Hold the brand until the next pattern heading so the two stay welded.
      if (pendingBrand !== null) open(null, `brand-${idx}`);
      pendingBrand = trimmed.slice(3);
      current = null;
    } else if (trimmed.startsWith('### ')) {
      open(trimmed.slice(4), `pattern-${idx}`);
    } else if (line.startsWith('- ')) {
      if (!current) open(null, `items-${idx}`);
      const raw = line.slice(2);
      const supp = parseItem(raw);
      current!.items.push(
        supp
          ? {
              key: `b-${idx}`,
              bullet: (
                <>
                  {renderSupplementName(supp)} — {supp.description}
                </>
              ),
            }
          : { key: `b-${idx}`, bullet: <>{raw}</> },
      );
    } else if (trimmed !== '') {
      if (!current) open(null, `text-${idx}`);
      current!.items.push({ key: `t-${idx}`, text: line });
    }
  });

  if (pendingBrand !== null) open(null, 'brand-trailing');
  return blocks;
}

// Consecutive product rows run together into one table; consecutive bullets
// into one <ul>; a prose line breaks whichever run is open. Mixed content only
// arises from an edited override.
function renderPdfItems(items: PdfItem[]): JSX.Element[] {
  const out: JSX.Element[] = [];
  let rows: ProductRow[] = [];
  let bullets: PdfItem[] = [];

  const flushRows = () => {
    if (rows.length === 0) return;
    out.push(<PdfProductTable key={`tbl-${out.length}`} products={rows} />);
    rows = [];
  };
  const flushBullets = () => {
    if (bullets.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} style={{ marginBottom: 0 }}>
        {bullets.map(i => (
          <li key={i.key}>{i.bullet}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  items.forEach(i => {
    if (i.legend) {
      flushRows();
      flushBullets();
      out.push(
        <div key={i.key} style={{ marginBottom: 6 }}>
          <PatternLegend patterns={i.legend} />
        </div>,
      );
    } else if (i.row) {
      flushBullets();
      rows.push(i.row);
    } else if (i.bullet) {
      flushRows();
      bullets.push(i);
    } else {
      flushRows();
      flushBullets();
      out.push(<div key={i.key}>{i.text}</div>);
    }
  });
  flushRows();
  flushBullets();
  return out;
}

function renderPdfBlock(
  block: SupplementsBlock<PdfItem>,
  isFirstOnPage: boolean,
): JSX.Element {
  return (
    <React.Fragment key={block.key}>
      {block.brandLabel && (
        <h4
          style={{
            marginTop: isFirstOnPage ? 0 : 18,
            marginBottom: 12,
            fontSize: '1.5rem',
          }}
        >
          <strong>{block.brandLabel}</strong>
        </h4>
      )}
      <div style={{ marginBottom: 14, marginLeft: 8 }}>
        {block.label !== null && (
          <h6
            style={{
              marginBottom: 6,
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            {block.label}
            {block.isContinuation ? ' (cont.)' : ''}
          </h6>
        )}
        {renderPdfItems(block.items)}
      </div>
    </React.Fragment>
  );
}

function readOverride(bloodworkAnalysis: BloodworkAnalysis): string {
  const note = bloodworkAnalysis?.bloodwork?.bloodworkNotes?.findNote(
    Section.SUPPLEMENTS,
  );
  return (note?.text ?? '').trimStart();
}

export class SupplementsSummaryPanel extends React.Component<Props, State> {
  state: State = {
    editing: false,
    draftText: '',
  };

  private editTextareaEl: HTMLTextAreaElement | null = null;

  // A draft belongs to the test it was opened on. The analysis screen keeps this
  // panel mounted and swaps the test under it when the practitioner analyzes
  // another row, so an edit left open on the previous test would otherwise be
  // saved over the new one's. See isSameBloodwork.
  componentWillReceiveProps(nextProps: Props) {
    if (
      !isSameBloodwork(
        nextProps.bloodworkAnalysis?.bloodwork,
        this.props.bloodworkAnalysis?.bloodwork,
      )
    ) {
      this.setState({ editing: false, draftText: '' });
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.editing && !prevState.editing) {
      this.fitTextareaToContent();
    }
  }

  private setEditTextareaRef = (el: HTMLTextAreaElement | null) => {
    this.editTextareaEl = el;
  };

  private fitTextareaToContent = () => {
    const el = this.editTextareaEl;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = 'hidden';
  };

  private get brandScope(): SupplementsBrandScope {
    return this.props.brandScope ?? 'otherBrands';
  }

  private computeGroups(): BrandGroup[] {
    const { bloodworkAnalysis, furtherConsiderations, enabledSupplementBrands } =
      this.props;
    return scopedGroups(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
      this.brandScope,
    );
  }

  // The practitioner's free-text override belongs to the Supplements panel —
  // the instance that owns the Edit button and seeds the draft. The Holobiome
  // scope never reads it, or the same override text would print twice: once
  // under the roadmap and again under Supplements.
  private readScopedOverride(): string {
    if (this.brandScope === 'systemicFormulas') return '';
    return readOverride(this.props.bloodworkAnalysis);
  }

  // Rough height (in px, html2pdf canvas units) of the rendered PDF panel.
  // Used by the parent container to decide whether the Disclaimer card fits
  // below Supplements on the same page or needs to break to its own page.
  // Errs on the high side — overestimating just pushes the disclaimer to a
  // fresh page when it might have fit; underestimating risks overflow.
  static estimatedPdfHeight(
    bloodworkAnalysis: BloodworkAnalysis,
    furtherConsiderations: FurtherConsiderations,
    enabledSupplementBrands?: string[],
    brandScope: SupplementsBrandScope = 'otherBrands',
  ): number {
    if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return 0;
    // Same rule as readScopedOverride: only the Supplements half can be
    // overridden, so only it estimates from the override text.
    const override =
      brandScope === 'systemicFormulas' ? '' : readOverride(bloodworkAnalysis);
    const CARD_OVERHEAD = 100;
    if (override) {
      let h = CARD_OVERHEAD;
      override.split('\n').forEach(line => {
        const t = line.trim();
        if (t.startsWith('## ')) h += 60;
        else if (t.startsWith('### ')) h += 40;
        else if (line.startsWith('- ')) h += 26;
        else if (t === '') h += 8;
        else h += 24;
      });
      return h;
    }
    const groups = scopedGroups(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
      brandScope,
    );
    if (groups.length === 0) return 0;
    let h = CARD_OVERHEAD;
    groups.forEach(({ sections }) => {
      h += 65; // brand heading
      sections.forEach(({ patterns, products }) => {
        h += 40; // system heading
        h += patterns.length * 18; // legend
        h += 24; // table header row
        h += products.length * PDF_ITEM_HEIGHT;
      });
    });
    return h;
  }

  private handleEditClick = () => {
    const override = this.readScopedOverride();
    const draft = override || groupsToText(this.computeGroups());
    this.setState({ editing: true, draftText: draft });
  };

  private handleCancel = () => {
    this.setState({ editing: false, draftText: '' });
  };

  private writeNote = (text: string) => {
    const notes = this.props.bloodworkAnalysis.bloodwork.bloodworkNotes;
    let note = notes.findNote(Section.SUPPLEMENTS);
    if (!note) note = notes.addNote(Section.SUPPLEMENTS, text);
    note.text = text;
  };

  // This panel flips from edit mode (tall textarea) to view mode, which shifts
  // layout while the global spinner modal is up — and that modal locks body
  // scrolling, so restoring a captured scrollY underneath it was clamped to 0
  // and left the report at the very top. Land back at the top of this panel
  // instead, once the save has resolved and the spinner is gone.
  private scrollBackToPanel = () => {
    scrollPanelIntoView('supplements-summary');
  };

  private handleSave = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    this.writeNote(this.state.draftText);
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(this.scrollBackToPanel)
      .catch(err => {
        console.error('Failed to save Supplements override', err);
        alert('Failed to save your changes. Please try again.');
        this.scrollBackToPanel();
      });
    this.setState({ editing: false, draftText: '' });
  };

  private handleReset = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    if (
      !window.confirm(
        'Clear your edits and restore the auto-generated Supplements list?',
      )
    ) {
      return;
    }
    this.writeNote('');
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(this.scrollBackToPanel)
      .catch(err => {
        console.error('Failed to reset Supplements override', err);
        alert('Failed to reset. Please try again.');
        this.scrollBackToPanel();
      });
    this.setState({ editing: false, draftText: '' });
  };

  render() {
    const {
      bloodworkAnalysis,
      furtherConsiderations,
      title = 'Supplements',
      forPdf,
      includeInPdf,
      onIncludeInPdfToggle,
      bloodworkEntryStore,
    } = this.props;
    const { editing, draftText } = this.state;

    if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) {
      return null;
    }

    const groups = this.computeGroups();
    const override = this.readScopedOverride();
    // One brand in the Holobiome scope, and the card header already names it,
    // so the brand heading would just repeat it.
    const showBrandHeadings = this.brandScope !== 'systemicFormulas';

    if (groups.length === 0 && !override) {
      return null;
    }

    const generatedBody = (
      <>
        {groups.map(({ brand, sections }) => (
          <div key={brand} style={{ marginBottom: 20 }}>
            {showBrandHeadings && (
              <h4 style={{ marginBottom: 12, fontSize: '1.5rem' }}>
                <strong>{brandLabel(brand)}</strong>
              </h4>
            )}
            <ProductTable sections={sections} />
          </div>
        ))}
      </>
    );

    const body = override ? renderOverride(override) : generatedBody;

    if (forPdf) {
      // Pre-paginated: one Card per page with an explicit break between them.
      // html2pdf's own keep-together pass can't be trusted this deep into a
      // long report — its page grid has drifted a full text line away from the
      // grid the canvas is actually sliced on — so the panel decides its own
      // page boundaries the way every other long section here does.
      const pages = packSupplementBlocks<PdfItem>({
        blocks: override
          ? blocksFromOverride(override)
          : blocksFromGroups(groups, showBrandHeadings),
        pageCapacity: PDF_PAGE_CAPACITY,
        cardOverhead: PDF_CARD_OVERHEAD,
        brandHeadingHeight: PDF_BRAND_HEADING,
        patternHeadingHeight: PDF_PATTERN_HEADING,
        itemHeight: PDF_ITEM_HEIGHT,
      });

      return (
        <>
          {pages.map((blocks, pageIdx) => (
            <React.Fragment key={`supplements-page-${pageIdx}`}>
              {pageIdx > 0 && (
                <>
                  <div className="html2pdf__page-break" />
                  {/* Same top buffer the container puts before the first page:
                      a forced break lands a hair above the true page edge, and
                      without the spacer the card header gets clipped. */}
                  <div style={{ height: 28 }} aria-hidden />
                </>
              )}
              <Card className="disclaimer-panel mb-5">
                <Card.Header className="py-1">
                  {pageIdx === 0 ? title : `${title} (cont.)`}
                </Card.Header>
                <Card.Body
                  className="disclaimer-body note-card-body"
                  style={{ paddingBottom: 12 }}
                >
                  {blocks.map((block, i) => renderPdfBlock(block, i === 0))}
                </Card.Body>
              </Card>
            </React.Fragment>
          ))}
        </>
      );
    }

    // The viewed body is wrapped in a bordered box so the Edit button below
    // it visually anchors to the content (mirrors the textarea+Save layout
    // used by the Insights notes pattern above this panel).
    const viewBody = (
      <>
        <div
          style={{
            border: '1px solid #ced4da',
            borderRadius: 4,
            padding: 12,
            backgroundColor: '#fff',
          }}
        >
          {body}
        </div>
        {bloodworkEntryStore && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <Button
              type="button"
              variant="primary"
              title="Edit Supplements"
              onClick={this.handleEditClick}
            >
              Edit
            </Button>
            {override && (
              <Button
                type="button"
                variant="outline-danger"
                title="Discard your edits and restore the auto-generated list with clickable supplement names"
                onClick={this.handleReset}
              >
                Reset to default
              </Button>
            )}
          </div>
        )}
      </>
    );

    const editBody = (
      <>
        <div
          style={{
            fontSize: '12px',
            color: '#6c757d',
            marginBottom: 6,
          }}
        >
          Formatting:&nbsp;
          <code>## Brand</code>&nbsp;for brand headings,&nbsp;
          <code>### Section</code>&nbsp;for section headings.
        </div>
        <textarea
          ref={this.setEditTextareaRef}
          value={draftText}
          onChange={e => this.setState({ draftText: e.target.value }, this.fitTextareaToContent)}
          rows={24}
          style={{
            width: '100%',
            // Same size as the panel it turns back into on Save (the app's body
            // text is 12px) — a textarea inherits neither font nor size.
            fontFamily: 'inherit',
            fontSize: '12px',
            padding: 8,
            borderRadius: 4,
            border: '1px solid #ced4da',
          }}
        />
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={this.handleSave}>
            Save
          </Button>
          <Button variant="secondary" onClick={this.handleCancel}>
            Cancel
          </Button>
          {override && (
            <Button
              variant="outline-danger"
              onClick={this.handleReset}
              style={{ marginLeft: 'auto' }}
              title="Discard your edits and restore the auto-generated list"
            >
              Reset to default
            </Button>
          )}
        </div>
      </>
    );

    return (
      // Anchored so Save can scroll back to the top of this panel.
      <Card id="supplements-summary">
        <Card.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>{title}</span>
          {!editing && onIncludeInPdfToggle && (
            <label
              htmlFor="include-in-pdf-supplements"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                margin: 0,
                fontWeight: 'normal',
                cursor: 'pointer',
                color: '#fff',
              }}
              title="Include this section in the Analysis PDF"
            >
              <span style={{ marginRight: '6px' }}>Include in PDF</span>
              <input
                id="include-in-pdf-supplements"
                type="checkbox"
                className="lsm-styled-checkbox"
                checked={!!includeInPdf}
                onChange={onIncludeInPdfToggle}
              />
            </label>
          )}
        </Card.Header>
        <Card.Body>{editing ? editBody : viewBody}</Card.Body>
      </Card>
    );
  }
}

// The Systemic Formulas half of the table, stripped of the Card chrome, the
// brand heading, and the Edit/override machinery — what the Holobiome Roadmap
// section drops in under its kit cards. Same collector, same columns, same
// month sequencing as the Supplements panel; the two just render disjoint
// halves of one list, so a product is never in both.
//
// This is where the roadmap's non-kit recommendations now live. They used to
// have their own "Add-On Products" table in the protocols panel, which this
// table subsumes: it carries the same product, description and origin, plus the
// pattern citation and the start month, and flags a non-kit product as "Add-on"
// in its Month cell.
// What the by-system view says about itself. Lives here beside the table it
// describes, but is RENDERED by the panel, up in the row with the view switch —
// see SupplementProtocolsPanel. The panel's header row was empty white space
// beside the switch while every view repeated a heading below a divider.
export const PRODUCT_TABLE_LEAD =
  'Every Systemic Formulas product the patterns identified in this test point to, grouped by system, worst first.';

export function ProductTableIntro({
  listEverywhere,
}: {
  listEverywhere: boolean;
}): JSX.Element {
  return (
    // Trimmed the same way, and for the same reason: how the ranking breaks
    // ties is in the popover, not over the table it orders.
    <div style={VIEW_INTRO}>
      Worst first is the Results Overview&apos;s own ranking.{' '}
      {listEverywhere
        ? 'A product several systems asked for is listed under each of them, and names the others beside its pattern number.'
        : 'A product several systems asked for is listed once, under the most concerning one.'}
    </div>
  );
}

export const HolobiomeProductTable: React.FC<{
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Absent in the PDF and wherever the practitioner can't edit: no column.
  clientList?: ClientListSelection;
  // Owned by the panel, because the control that flips it now sits under the
  // view switch rather than over this table.
  listEverywhere?: boolean;
  // Absent in the PDF: an underline that does nothing on paper.
  onSectionClick?: (section: Section) => void;
}> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  clientList,
  listEverywhere = true,
  onSectionClick,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  const groups = scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    'systemicFormulas',
    listEverywhere,
  );
  if (groups.length === 0) return null;

  return (
    // No top rule and no heading: both were chrome for a block that used to
    // announce itself halfway down the panel. The heading is up in the row with
    // the view switch now, which is where the reader already is.
    <div style={{ width: '100%' }}>
      {/* One card per system rather than one table for all of them — see
          SystemCard. The brand loop stays because scopedGroups returns brand
          groups, but this scope only ever has Systemic Formulas in it. */}
      {groups.map(({ brand, sections }) => (
        <React.Fragment key={brand}>
          {sections.map(group => (
            <SystemCard
              key={String(group.section)}
              group={group}
              clientList={clientList}
              onSectionClick={onSectionClick}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─────────────────── Top Priority (screen + PDF) ───────────────────
//
// The shortlist, as a single ranked table. NOT one card per system like the
// by-system view: the whole claim of this view is that these seven sit in one
// order across the report, and boxing them by system would take that order
// back apart. The rank number is a column for the same reason — it is the
// answer, not decoration, and a reader scanning for "what first" should not
// have to infer it from row position.
//
// Column widths are its own rather than the shared COLGROUP's: this table
// carries a rank the other two don't, and cites patterns by count as well as
// number, so its Why column is wider than their Pattern column.
// No month here either, for the reason the by-system table has none: a start
// month is a position in the five-month program, and this is a shortlist, not a
// schedule. See COL_WIDTHS.
const TOP_COL_WIDTHS = { rank: 4, product: 13, description: 58, why: 25 };
const TOP_CLIENT_LIST_COL_WIDTHS = {
  rank: 4,
  product: 13,
  description: 55,
  why: 22,
  clientList: 6,
};

const topColgroup = (widths: { [column: string]: number }): JSX.Element => (
  <colgroup>
    {Object.keys(widths).map(column => (
      <col key={column} style={{ width: `${widths[column]}%` }} />
    ))}
  </colgroup>
);

// The rank cell. Gray and light rather than bold: the number orders the list,
// and seven bold numerals down the left would out-shout the product names they
// are ordering.
const RANK_CELL: React.CSSProperties = {
  textAlign: 'center',
  color: '#8b96a3',
  fontVariantNumeric: 'tabular-nums',
};

// One shortlisted product.
//
// `patternRefs` are 1-based indices into the legend above the table — the same
// convention the by-system cards use, except the legend here is drawn from
// every system at once rather than one card's.
function topPriorityRow(
  p: TopPriority,
  patternRefs: number[],
  rank: number,
  isLast: boolean,
  clientList?: ClientListSelection,
  onSectionClick?: (section: Section) => void,
): JSX.Element {
  const cell = isLast ? { ...CELL, borderBottom: 'none' } : CELL;
  return (
    <tr key={p.name}>
      <td style={{ ...cell, ...RANK_CELL, ...NOWRAP }}>{rank}</td>
      {/* Wraps, for the same reason the by-system table's Product column does:
          at this width the longest name only just fits. */}
      <td style={cell}>{renderSupplementName(p)}</td>
      <td style={cell}>{p.description}</td>
      {/* Why it is on the list, in the order the ranking weighs it: how many
          patterns cite it, then which patterns, then which systems asked. The
          count is spelled out rather than left for the reader to get by
          counting the numerals beside it — it is the primary sort key, so it
          is the thing that has to be legible at a glance. */}
      <td style={{ ...cell, whiteSpace: 'normal' }}>
        <span style={{ color: '#495057' }}>
          {p.patterns.length} pattern{p.patterns.length === 1 ? '' : 's'}
        </span>
        {patternRefs.length > 0 && (
          <span style={{ color: '#8b96a3' }}> ({patternRefs.join(', ')})</span>
        )}
        <div style={{ color: '#8b96a3' }}>
          from{' '}
          {/* Each system inline-block with its own leading separator, so a
              product several systems asked for wraps whole rather than
              stranding the dot at a line end. Same treatment as the by-system
              table's "also …" note. */}
          {p.sections.map((section, i) => (
            <span
              key={section}
              style={{ display: 'inline-block', marginRight: 4 }}
            >
              {i > 0 && '· '}
              <SectionJump
                section={section}
                label={sectionShortLabel(section)}
                onSectionClick={onSectionClick}
              />
            </span>
          ))}
        </div>
      </td>
      {clientList && (
        <td style={{ ...cell, textAlign: 'center' }}>
          <input
            type="checkbox"
            className="lsm-styled-checkbox lsm-styled-checkbox--on-light"
            checked={clientList.isSelected(p.name)}
            onChange={() => clientList.toggle(p.name)}
            aria-label={`Include ${p.name} in the client's recommended supplements`}
            title={`Include ${p.name} in the client's recommended supplements`}
          />
        </td>
      )}
    </tr>
  );
}

// The legend for the shortlist: every pattern the seven cite, numbered once
// across the whole table.
//
// Cross-system, unlike a system card's legend, so the numbers are assigned in
// the order the RANKED rows introduce them — the reader meets number 1 in the
// first row, not in whichever system happens to sort first. Bare pattern names
// with no system prefix: the census dedupes by name, so a pattern two systems
// share has no single system to attribute it to, and the Why column already
// names the systems that asked.
export function topPriorityLegend(
  priorities: TopPriority[],
  // Seed, when the caller already has the whole list's legend. A PDF
  // continuation page numbers against the WHOLE shortlist's legend, not its own
  // slice of it, or its rows would cite number 1 for a different pattern than
  // page one did.
  seed?: string[],
): { patterns: string[]; refsByProduct: Map<string, number[]> } {
  const patterns: string[] = seed ? [...seed] : [];
  const refsByProduct = new Map<string, number[]>();
  priorities.forEach(priority => {
    refsByProduct.set(
      priority.name,
      priority.patterns.map(pattern => {
        let idx = patterns.indexOf(pattern);
        if (idx === -1) idx = patterns.push(pattern) - 1;
        return idx + 1; // 1-based, as displayed
      }),
    );
  });
  return { patterns, refsByProduct };
}

// The first sentence, which rides on the heading line rather than opening the
// paragraph — see ViewHeading in supplement-protocols-panel. A plain string, so
// the heading can set it inline beside the title.
export const topPriorityLead = (count: number): JSX.Element => (
  <>
    The {count} product{count === 1 ? '' : 's'} to start with, drawn from the{' '}
    <strong>By System</strong> list.
  </>
);

// Everything after that first sentence: how the ranking was arrived at, in the
// words a practitioner would use to defend it to a client. Shared by the screen
// view and the PDF card — the ranking is the one thing about this view that
// isn't self-evident from looking at it, so the printed copy explaining it must
// not drift from the on-screen copy.
// The report's own body color at the Roadmap headline's size, not a gray
// caption a step under it.
//
// The three views sat in the same slot at two different weights: the Roadmap's
// coverage line in the report's near-black, the two list views' intros in
// #6c757d. Switching views changed the voice of the panel, and the gray was the
// harder of the two to read for what is a full sentence of explanation rather
// than a label. Black for all three.
export const VIEW_INTRO: React.CSSProperties = {
  fontSize: '13.5px',
  color: '#212529',
  lineHeight: 1.45,
  marginBottom: '8px',
};

export function TopPriorityIntro(): JSX.Element {
  return (
    <div style={VIEW_INTRO}>
      {/* TRIMMED. The full rule — what a first choice is, what happens when a
          worse system has already claimed it, how the cap stretches, what the
          note under the table counts — is in the section's own help popover.
          Four lines of it above a seven-row table made the explanation the
          densest thing on the panel. Two sentences say what the order is; the
          popover says why. */}
      Every system marked <strong>Priority</strong> or{' '}
      <strong>Support</strong> gets its own first choice, most concerning system
      first, plus any other vitamin or mineral at <strong>Priority</strong>. The
      slots left over go to the products the most patterns point at.
    </div>
  );
}

// THE LINE BETWEEN THE TWO PASSES.
//
// The Why column states a pattern count, and down the list those counts read
// 1, 3, 2, 1, 7, 2 — scrambled. Every other numeric column in this report is
// sorted, so the column reads as mis-sorted, and the 7 sitting below the 1 is
// the jarring bit. It isn't mis-sorted: the rows above the line are chosen by
// COVERAGE, one per system on that system's rank, and the ones below by
// CITATIONS. The column can only be ordered within each group.
//
// A rule and one line of text, no new column. The rule is a shade stronger than
// the row separators and the row above it drops its own, so the two don't read
// as a double line.
//
// IT ONLY NAMES THE HALF BELOW IT. The line used to describe both sides ("the
// products above are each system's own first choice, below them …"), which was
// twenty words restating what the intro three inches above the table has
// already said in full. The divider's actual job is narrower: mark where the
// pattern-count column starts being sorted again, so the reader stops reading
// it as broken. Naming the lower half does that on its own.
//
// It also drops a problem rather than solving one. The old sentence needed a
// singular and a plural form, because the block above the rule is a single row
// on a report where one system qualifies — and once the priority-marker pass
// could put two vitamins up there, "each system's" described a one-per-system
// block that was no longer true either. A caption that says nothing about the
// rows above it needs neither fix.
function passDividerRow(columns: number): JSX.Element {
  return (
    <tr key="top-priority-pass-divider">
      {/* Not italic: set in italic this reads as an aside, and it is the
          caption that makes the column above it legible. */}
      <td
        colSpan={columns}
        style={{
          borderTop: '1px solid #adb5bd',
          padding: '8px 10px 6px 0',
          fontSize: '11.5px',
          color: '#6c757d',
          lineHeight: 1.4,
        }}
      >
        Below, ranked by how many patterns point at them.
      </td>
    </tr>
  );
}

// The table itself, without the surrounding heading — shared by the screen view
// and the PDF card so the printed shortlist can't drift from the on-screen one.
export function TopPriorityTable({
  priorities,
  clientList,
  onSectionClick,
  legendPatterns,
  showLegend = true,
  startRank = 1,
  withProductHint = true,
}: {
  priorities: TopPriority[];
  clientList?: ClientListSelection;
  onSectionClick?: (section: Section) => void;
  // The legend to number against. Defaults to the one these rows build, and is
  // passed explicitly when the PDF splits the rows across pages: numbering a
  // continuation page off its own slice would restart at 1 and cite different
  // patterns than page one.
  legendPatterns?: string[];
  showLegend?: boolean;
  // Rank of the first row, so a continuation page carries on counting.
  startRank?: number;
  // The info mark on the Product heading. Off in the PDF — see
  // PRODUCT_LINK_HINT.
  withProductHint?: boolean;
}): JSX.Element {
  const built = topPriorityLegend(priorities, legendPatterns);
  const patterns = legendPatterns ?? built.patterns;
  const refsByProduct = built.refsByProduct;
  return (
    <>
      {showLegend && patterns.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <PatternLegend patterns={patterns} />
        </div>
      )}
      {/* Horizontal scroll on this table's own container, so a narrow viewport
          never makes the whole report scroll sideways. */}
      <div style={{ overflowX: 'auto' }}>
        <table style={TABLE_STYLE}>
          {topColgroup(
            clientList ? TOP_CLIENT_LIST_COL_WIDTHS : TOP_COL_WIDTHS,
          )}
          <thead>
            <tr>
              {/* The rank column heads with a hash, not "Rank": the column is
                  four characters wide and the word would set its width. */}
              <th style={{ ...HEAD_CELL, textAlign: 'center' }}>#</th>
              <ProductHeadCell withHint={withProductHint} />
              <th style={HEAD_CELL}>Description</th>
              <th style={HEAD_CELL}>Why</th>
              {clientList && (
                <th style={{ ...HEAD_CELL, textAlign: 'center' }}>
                  Client List
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {priorities.map((priority, i) => {
              // The last row of the coverage block drops its separator so the
              // divider's own rule doesn't double up against it.
              const opensDepth =
                i > 0 &&
                priority.pass === 'depth' &&
                priorities[i - 1].pass === 'coverage';
              const row = topPriorityRow(
                priority,
                refsByProduct.get(priority.name) ?? [],
                startRank + i,
                i === priorities.length - 1 ||
                  (i + 1 < priorities.length &&
                    priorities[i + 1].pass === 'depth' &&
                    priority.pass === 'coverage'),
                clientList,
                onSectionClick,
              );
              if (!opensDepth) return row;
              return (
                <React.Fragment key={`${priority.name}-block`}>
                  {passDividerRow(clientList ? 5 : 4)}
                  {row}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// WHAT DIDN'T FIT, stated rather than left to be noticed.
//
// A shortlist that silently omits a system is worse than a longer one: a
// practitioner who sees Thyroid flagged in the report and no thyroid product
// here has no way to tell whether none exists or whether it just missed the
// cut. One line answers that, and points at the view that has the rest.
//
// Nothing when nothing was cut — a note saying "0 more systems" is noise.
export function UnrepresentedNote({ count }: { count: number }): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <div
      style={{
        fontSize: '11.5px',
        color: '#6c757d',
        lineHeight: 1.45,
        marginTop: '10px',
      }}
    >
      {count} other system{count === 1 ? '' : 's'} recommend
      {count === 1 ? 's' : ''} supplements that are not on this list. See{' '}
      <strong>By System</strong> for the full set.
    </div>
  );
}

// The Top Priority view of the Holobiome Roadmap: the shortlist, its heading
// and its dosing note — what the protocols panel renders when the view switch
// is on Top Priority, and the same block the PDF prints ahead of the kits.
export const HolobiomeTopPriorityTable: React.FC<{
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Absent in the PDF and wherever the practitioner can't edit: no column.
  clientList?: ClientListSelection;
  // Absent in the PDF: an underline that does nothing on paper.
  onSectionClick?: (section: Section) => void;
}> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  clientList,
  onSectionClick,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  const priorities = collectTopPriorities(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  if (priorities.length === 0) return null;
  const unrepresented = unrepresentedSystemCount(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );

  return (
    // Heading and intro are up in the view-switch row (see
    // SupplementProtocolsPanel); this is the table alone.
    <div style={{ width: '100%' }}>
      <TopPriorityTable
        priorities={priorities}
        clientList={clientList}
        onSectionClick={onSectionClick}
      />
      <UnrepresentedNote count={unrepresented} />
    </div>
  );
};
